import { MatchEvidencePanel } from './MatchEvidence';
import React from 'react';
import {
  LedgerRow,
  buildLedgerRows,
  hasSettledOutcome,
  ledgerFromMessage,
  lineChip,
  researchableTerms,
} from '../../../utils/requestLines';
import { flushSync, createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { matchBand, MATCH_BAND_LABEL, MatchBand } from '../../../utils/diagramTag';
import { LayoutGrid, Table2, Check, ShoppingCart, Loader2, Download, ChevronRight, ChevronDown, ZoomIn, ImageOff, Search, SearchX, Car, AlertTriangle, Pencil, ClipboardCheck, Maximize2, Sparkles, Trash2, Undo2, Plus, X } from 'lucide-react';
import { PartResult, RequestLine } from './types';
import { Callout } from '../../../models/callout';
import ImageWithCallouts from '../../common/ImageWithCallouts';
import QuerySearchView, { ComplementarySkeleton } from './QuerySearchView';
import { translateBatch, BatchTranslationItem, TranslationBatchManager } from '../../../services/TranslationService';
import { useOrder } from '../../../contexts/OrderContext';
import { useAuth } from '../../../contexts/AuthContext';
import { partNamesMatch, normPartName, SummaryItem } from './PartsTrackerPanel';
import { expandAttribute, positionChipsFromExtra, positionChipSlug, isPartNumberQuery, composeNumberRequestLabel, displayPartRef, splitPartAndRef, requestedOriginalTerm } from '../../../utils/partLabel';
import { parseCatalogQuantity } from '../../../utils/quantity';
import SupplierSuggestionRow, { SupplierSuggestion, trimSuggestionsForDisplay } from './SupplierSuggestionRow';
import SourceBadge from './SourceBadge';
import SupersessionIndicator from '../../common/SupersessionIndicator';
import chatService from '../../../services/ChatService';
import { buildPartEditMarker } from '../../../utils/partEdits';

// Re-export for existing importers (DataSourceSuggestions, BotMessage).
export type { SupplierSuggestion } from './SupplierSuggestionRow';

// Strings that must NEVER be treated as a part identity. "•••" is what
// displayPartRef renders a guest-mode masked ref-token as — it is the same
// literal for every part, so using it as a key made one cart item match every
// row. Belt-and-braces: identity paths use the raw value, and these are refused
// even if a masked display string ever reaches one again.
const NON_IDENTIFYING_REFS = new Set(['n/a', '•••', '-', '—', '']);

const ExpertDecisionReceipt: React.FC<{
  line?: RequestLine;
  t: (key: string, opts?: any) => string;
  // Open on load only where the reader asked for it (the popup). On a result
  // card it stays folded: a record most people read once, if at all.
  open?: boolean;
}> = ({ line, t, open }) => {
  const expert = line?.expert_case;
  if (!expert) return null;
  const evidence = (expert.evidence ?? []).filter(Boolean);
  const missing = (expert.missing_facts ?? []).filter(Boolean);
  const selected = (expert.selected_parts ?? []).filter(p => p?.part_number || p?.name);
  const assumptions = (expert.assumptions ?? []).filter(f => f?.dimension || f?.value);
  const alternatives = (expert.alternatives ?? []).filter(p => p?.part_number || p?.name);
  const references = (expert.catalog_references ?? []).filter(r => r?.group_id || r?.section_id || r?.callout);
  const stated = (expert.stated_facts ?? []).filter(Boolean).map(f => f.replace(':', ': '));
  const checkedVin = (expert.checks ?? []).some(c => c?.kind === 'vin_epc');
  const assessment = ({
    catalog_supported: t('chat.multiPart.expertSupported', { defaultValue: 'Catalog-supported' }),
    conditional: t('chat.multiPart.expertConditional', { defaultValue: 'Conditional — verify before ordering' }),
    user_confirmed: t('chat.multiPart.expertUserConfirmed', { defaultValue: 'Confirmed by your answer' }),
    needs_answer: t('chat.multiPart.expertNeedsAnswer', { defaultValue: 'Needs your answer' }),
    unconfirmed: t('chat.multiPart.expertUnconfirmed', { defaultValue: 'No match confirmed' }),
    not_in_catalog: t('chat.multiPart.lineNotInCatalog', { defaultValue: 'Not in the OE catalog' }),
    error: t('chat.multiPart.reasonCatalogUnavailable', { defaultValue: 'Catalog unavailable' }),
  } as Record<string, string>)[expert.decision_status || ''] || '';
  const next = ({
    search_catalog: t('chat.multiPart.expertNextSearch', { defaultValue: 'Search the VIN-specific catalog' }),
    review_result: t('chat.multiPart.expertNextReview', { defaultValue: 'Review the selected OE part and evidence' }),
    answer_question: t('chat.multiPart.expertNextAnswer', { defaultValue: 'Confirm the distinguishing vehicle detail' }),
    refine_request: t('chat.multiPart.expertNextRefine', { defaultValue: 'Add a location, photo, marking, or function to refine the search' }),
    check_connected_sources: t('chat.multiPart.expertNextSources', { defaultValue: 'Check connected supplier sources' }),
    retry_catalog: t('chat.multiPart.expertNextRetry', { defaultValue: 'Retry when the catalog is available' }),
    none: '',
  } as Record<string, string>)[expert.next_step || ''] || '';
  const hasDetail = checkedVin || evidence.length > 0 || missing.length > 0 || selected.length > 0 || expert.question || next;
  if (!hasDetail) return null;
  const isCaution = ['conditional', 'needs_answer', 'unconfirmed', 'not_in_catalog', 'error'].includes(expert.decision_status || '');
  const accent = isCaution ? '#b7791f' : '#397367';
  const tint = isCaution ? '#fffaf0' : '#f2fbf8';
  const border = isCaution ? '#f1d7a5' : '#bfe3d9';
  const workflow = [
    { label: t('chat.multiPart.expertUnderstood', { defaultValue: 'Understood' }), done: !!expert.understood_as },
    { label: t('chat.multiPart.expertChecked', { defaultValue: 'VIN checked' }), done: checkedVin },
    { label: t('chat.multiPart.expertCompared', { defaultValue: 'Candidates compared' }), done: selected.length > 0 || (expert.candidate_count ?? 0) > 0 },
    { label: t('chat.multiPart.expertAssessment', { defaultValue: 'Assessment' }), done: !!expert.decision_status && expert.decision_status !== 'investigating' },
  ];
  return (
    <section
      aria-label={t('chat.multiPart.expertReceipt', { defaultValue: 'Expert verdict' })}
      style={{ fontSize: '12px', color: '#353330', background: tint, border: `1px solid ${border}`, borderLeft: `4px solid ${accent}`, borderRadius: '10px', padding: '10px 12px' }}
    >
      <details open={open}>
      <summary style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '7px', cursor: 'pointer', userSelect: 'none' }}>
        <strong style={{ fontSize: '13px', color: accent }}>{t('chat.multiPart.expertReceipt', { defaultValue: 'Expert verdict' })}</strong>
        {assessment && <span style={{ fontSize: '10px', fontWeight: 700, color: accent, border: `1px solid ${border}`, background: '#fff', borderRadius: '999px', padding: '2px 7px' }}>{assessment}</span>}
      </summary>
      <div style={{ marginTop: '8px' }}>

      <div aria-label={t('chat.multiPart.expertWorkflow', { defaultValue: 'Expert workflow' })} style={{ display: 'flex', flexWrap: 'wrap', gap: '5px', marginBottom: '9px' }}>
        {workflow.map((step, index) => (
          <React.Fragment key={`${step.label}-${index}`}>
            {index > 0 && <span aria-hidden="true" style={{ color: '#aaa49b', alignSelf: 'center' }}>›</span>}
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', color: step.done ? '#3d5f56' : '#8c877f', background: step.done ? '#fff' : 'transparent', border: `1px solid ${step.done ? border : '#dedbd5'}`, borderRadius: '999px', padding: '3px 7px', fontSize: '10px', fontWeight: 650 }}>
              {step.done ? <Check size={11} aria-hidden="true" /> : <span aria-hidden="true">○</span>}{step.label}
            </span>
          </React.Fragment>
        ))}
      </div>

      <div style={{ display: 'grid', gap: '6px', lineHeight: 1.45 }}>
        {expert.understood_as && <div><strong>{t('chat.multiPart.expertUnderstood', { defaultValue: 'Understood as' })}:</strong> {expert.understood_as}</div>}
        {selected.length > 0 && <div><strong>{t('chat.multiPart.expertSelected', { defaultValue: 'Recommendation' })}:</strong> {selected.map(p => [p.part_number, p.name].filter(Boolean).join(' · ')).join('; ')}</div>}
        {evidence.length > 0 && <div><strong>{t('chat.multiPart.expertWhy', { defaultValue: 'Why it fits' })}:</strong> {evidence.slice(0, 2).join(' · ')}</div>}
        {missing.length > 0 && <div style={{ color: '#8a5a0a' }}><strong>{t('chat.multiPart.expertUnresolved', { defaultValue: 'Confirm before ordering' })}:</strong> {missing.join(' · ')}</div>}
        {expert.question && <div style={{ color: '#8a5a0a' }}><strong>{t('chat.multiPart.expertQuestion', { defaultValue: 'Specialist question' })}:</strong> {expert.question}</div>}
        {next && <div><strong>{t('chat.multiPart.expertNext', { defaultValue: 'Next step' })}:</strong> {next}</div>}
      </div>

      {(stated.length > 0 || checkedVin || evidence.length > 2 || assumptions.length > 0 || references.length > 0 || alternatives.length > 0 || (expert.candidate_count ?? 0) > 1) && (
        <details style={{ marginTop: '8px', paddingTop: '7px', borderTop: `1px solid ${border}` }}>
          <summary style={{ cursor: 'pointer', color: accent, fontWeight: 700, userSelect: 'none' }}>
            {t('chat.multiPart.expertFullRecord', { defaultValue: 'Evidence and catalog record' })}
          </summary>
          <div style={{ marginTop: '7px', display: 'grid', gap: '5px' }}>
            {stated.length > 0 && <div><strong>{t('chat.multiPart.expertUserStated', { defaultValue: 'You specified' })}:</strong> {stated.join(' · ')}</div>}
            {checkedVin && <div><strong>{t('chat.multiPart.expertChecked', { defaultValue: 'Checked' })}:</strong> {t('chat.multiPart.expertVinCatalog', { defaultValue: 'VIN-specific OE catalog, diagrams, and fitment details' })}</div>}
            {evidence.length > 2 && <ul style={{ margin: '0 0 0 17px', padding: 0 }}>{evidence.slice(2).map((item, i) => <li key={i}>{item}</li>)}</ul>}
            {assumptions.length > 0 && <div><strong>{t('chat.multiPart.expertCatalogResolved', { defaultValue: 'Catalog choice to verify' })}:</strong> {assumptions.map(f => [f.dimension, f.value].filter(Boolean).join(': ')).join(' · ')}</div>}
            {references.length > 0 && <div><strong>{t('chat.multiPart.expertCatalogReference', { defaultValue: 'Catalog reference' })}:</strong> {references.map(r => [r.group_id, r.section_id, r.callout].filter(Boolean).join(' / ')).join('; ')}</div>}
            {alternatives.length > 0 && <div><strong>{t('chat.multiPart.expertAlternativesRejected', { defaultValue: 'Alternatives not selected' })}:</strong><ul style={{ margin: '3px 0 0 17px', padding: 0 }}>{alternatives.map((part, i) => <li key={i}>{[part.part_number, part.name].filter(Boolean).join(' · ')}{part.reason ? ` — ${part.reason}` : ''}</li>)}</ul></div>}
            {expert.candidate_count != null && expert.candidate_count > 1 && <div><strong>{t('chat.multiPart.expertCompared', { defaultValue: 'Compared' })}:</strong> {t('chat.multiPart.expertCandidateCount', { defaultValue: '{{count}} catalog candidates', count: expert.candidate_count })}</div>}
          </div>
        </details>
      )}
      </div>
      </details>
    </section>
  );
};

// "Expert verdict" — the decision receipt, opened in a popup anchored to a small
// chip in the PROB. column. It used to be a full-width row under every result:
// a whole line of the table spent on a record most people read once, if at all.
const POPUP_WIDTH = 360;

const ExpertVerdictChip: React.FC<{
  line?: RequestLine;
  t: (key: string, opts?: any) => string;
}> = ({ line, t }) => {
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  // Fixed viewport coords: the popup is portalled out of the table, which clips
  // (overflow) and would otherwise cut it off at the cell.
  const [coords, setCoords] = React.useState<{ top: number; left: number; above: boolean } | null>(null);
  // Fixed positioning drifts away from its chip on scroll and outlives a click
  // elsewhere — close on both, and on Escape.
  React.useEffect(() => {
    if (!coords) return;
    const close = () => setCoords(null);
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    document.addEventListener('click', close);
    document.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
      document.removeEventListener('click', close);
      document.removeEventListener('keydown', onKey);
    };
  }, [coords]);
  if (!line?.expert_case) return null;
  const label = t('chat.multiPart.expertReceipt', { defaultValue: 'Expert verdict' });
  const toggle = (e: React.MouseEvent) => {
    e.stopPropagation();   // the row itself is clickable — don't select the part
    e.preventDefault();
    if (coords) { setCoords(null); return; }
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const above = rect.bottom > window.innerHeight * 0.6;
    setCoords({
      top: above ? rect.top - 6 : rect.bottom + 6,
      left: Math.min(
        Math.max(8, rect.left + rect.width / 2 - POPUP_WIDTH / 2),
        Math.max(8, window.innerWidth - POPUP_WIDTH - 8),
      ),
      above,
    });
  };
  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={toggle}
        title={label}
        aria-label={label}
        aria-expanded={!!coords}
        style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          width: '22px', height: '22px', padding: 0, cursor: 'pointer',
          color: '#397367', backgroundColor: '#f2fbf8',
          border: `1px solid ${coords ? '#397367' : '#bfe3d9'}`, borderRadius: '50%',
        }}
      >
        <ClipboardCheck size={12} />
      </button>
      {coords && createPortal(
        <div
          role="dialog"
          aria-label={label}
          onClick={e => e.stopPropagation()}
          style={{
            position: 'fixed', top: coords.top, left: coords.left,
            transform: coords.above ? 'translateY(-100%)' : 'none',
            width: `${POPUP_WIDTH}px`, maxWidth: 'calc(100vw - 16px)',
            maxHeight: '60vh', overflowY: 'auto',
            borderRadius: '10px', boxShadow: '0 10px 30px rgba(0,0,0,0.18)',
            zIndex: 99999, textAlign: 'left',
          }}
        >
          <ExpertDecisionReceipt line={line} t={t} open />
        </div>,
        document.body,
      )}
    </>
  );
};


// ── Client-side data-source (vector) search for not-found rows ───────────────
// The pipeline attaches supplier_suggestions to the search payload, but some
// paths miss it (older messages, resume fast-paths). For those rows the table
// asks the backend directly. Module-level so repeated renders and sibling
// messages never re-query the same term; `null` = gate state unknown yet.
// `vehicle` is the context the backend reported it ranked with (shown as the
// "matched for …" hint), kept with the hits so cache hits can show it too.
const dsSearchCache = new Map<string, { hits: SupplierSuggestion[]; vehicle: string }>();
/** Lookups in flight, keyed like the cache — shared by every re-run of the effect. */
const dsSearchInFlight = new Map<string, Promise<{ hits: SupplierSuggestion[]; vehicle: string } | null>>();
let dsSearchEnabled: boolean | null = null;

// ── Refine Popup ─────────────────────────────────────────────────────────────
// Each item carries the original assumption text (for backend context) and the
// user-editable value (pre-filled from the assumption, ready to be modified).
export interface RefineItem {
  original: string;   // the request's identity for the backend (the customer's own term)
  value: string;      // editable text, pre-filled with what the row reads as
  /** What the row reads as ("Bumper, Front") — shown as CURRENT and pre-filled,
   *  while `original` ("bumper") stays the identity the backend edits by. */
  label?: string;
}

export const RefinePopup: React.FC<{
  items: RefineItem[];
  t: (key: string, opts?: any) => string;
  onSubmit: (items: RefineItem[]) => void;
  onClose: () => void;
}> = ({ items, t, onSubmit, onClose }) => {
  const [values, setValues] = React.useState<string[]>(items.map(i => i.value));
  // Rows struck out with the bin: sent as a removal (value "") — see partEdits.ts.
  const [removed, setRemoved] = React.useState<boolean[]>(items.map(() => false));
  // Parts typed into "Add a part": sent as additions (original "").
  const [added, setAdded] = React.useState<string[]>([]);
  const firstRef = React.useRef<HTMLInputElement>(null);
  const addRef = React.useRef<HTMLInputElement>(null);

  // Auto-focus + select the first textarea on open
  React.useEffect(() => {
    const ta = firstRef.current;
    if (ta) { ta.focus(); ta.select(); }
  }, []);

  // Close on Escape
  React.useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const handleChange = (idx: number, val: string) => {
    setValues(prev => prev.map((v, i) => (i === idx ? val : v)));
  };

  const toggleRemoved = (idx: number) => setRemoved(prev => prev.map((r, i) => (i === idx ? !r : r)));
  const addRow = () => {
    setAdded(prev => [...prev, '']);
    setTimeout(() => addRef.current?.focus(), 0);
  };
  const handleAddedChange = (idx: number, val: string) => setAdded(prev => prev.map((v, i) => (i === idx ? val : v)));
  const dropAdded = (idx: number) => setAdded(prev => prev.filter((_, i) => i !== idx));

  // Every row goes out as one edit: a struck row as a removal, a changed row
  // as a rename, a typed "Add a part" as an addition. A row whose box was
  // merely emptied is left unchanged — removing is the bin, never a blank.
  const handleSubmit = () => {
    const refined: RefineItem[] = [];
    items.forEach((item, idx) => {
      if (removed[idx]) refined.push({ original: item.original, value: '' });
      // A row left as it reads is no edit: it goes out as the no-op
      // original → original (dropped by the marker), never as a rename to
      // our own reading of it.
      else if (values[idx].trim()) refined.push({ original: item.original, value: values[idx].trim() === shownOf(item) ? item.original : values[idx].trim() });
    });
    added.forEach(v => { if (v.trim()) refined.push({ original: '', value: v.trim() }); });
    if (refined.length === 0) return;
    onSubmit(refined);
    onClose();
  };

  const shownOf = (item: RefineItem) => (item.label || item.original).trim();
  const hasChanges = removed.some(Boolean)
    || values.some((v, idx) => !removed[idx] && v.trim() && v.trim() !== shownOf(items[idx]))
    || added.some(v => v.trim());

  // Rendered at <body>: the popup is fixed and z-indexed, but inside the
  // tracker pill (its own stacking context) the fixed chat composer still
  // painted over the backdrop and the dim/blur stopped above it (live).
  return createPortal(
    /* Backdrop */
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        backgroundColor: 'rgba(31, 29, 26, 0.38)',
        backdropFilter: 'blur(2px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '16px',
      }}
    >
      {/* Dialog */}
      <div
        onClick={e => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: '560px',
          backgroundColor: '#ffffff',
          borderRadius: '14px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
          overflow: 'hidden',
          display: 'flex', flexDirection: 'column',
          maxHeight: 'min(80vh, 620px)',
        }}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px 10px 16px', borderBottom: '1px solid #f0ede9', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 20 20" fill="#6866ff">
              <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
            </svg>
            <span style={{ fontSize: '13px', fontWeight: 700, color: '#201e1a' }}>
              {t('chat.partEdit.title', { defaultValue: 'Edit requested parts' })}
            </span>
          </div>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px', color: '#aaa', display: 'flex', alignItems: 'center' }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
        </div>

        {/* Compact replacement rows: current value → edited value. */}
        <div style={{ padding: '14px 16px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <p style={{ fontSize: '12px', lineHeight: 1.45, color: '#777', margin: '0 0 4px' }}>
            {items.length === 1
              ? t('chat.partEdit.hintOne', { defaultValue: 'Change the description to replace this search, remove it, or add another part.' })
              : t('chat.partEdit.hintMany', { defaultValue: 'Change, remove or add only what you need. Unchanged parts and results stay in place.' })}
          </p>
          {items.map((item, idx) => (
            <div key={idx} data-testid="part-edit-row" data-removed={removed[idx] ? 'true' : undefined} style={{
              display: 'grid', gridTemplateColumns: 'minmax(120px, 0.8fr) 20px minmax(180px, 1.2fr) 28px',
              gap: '8px', alignItems: 'center', padding: '8px',
              border: `1px solid ${removed[idx] ? '#f3c9c4' : '#ece9e4'}`, borderRadius: '10px',
              backgroundColor: removed[idx] ? '#fff6f5' : '#fbfaf8',
            }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: '9px', color: '#999', marginBottom: '3px', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 650 }}>
                  {t('chat.partEdit.current', { defaultValue: 'Current' })}
                </div>
                <div style={{ fontSize: '12px', color: '#615e58', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textDecoration: removed[idx] ? 'line-through' : 'none' }} title={item.label && item.label !== item.original ? `${item.label} (${item.original})` : item.original}>
                  {(item.label || item.original).replace(/\*\*/g, '')}
                </div>
              </div>
              <span aria-hidden="true" style={{ color: '#aaa', textAlign: 'center', fontSize: '15px' }}>→</span>
              <div>
                <label htmlFor={`part-edit-${idx}`} style={{ fontSize: '9px', color: removed[idx] ? '#b42318' : '#6866ff', display: 'block', marginBottom: '3px', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 650 }}>
                  {removed[idx]
                    ? t('chat.partEdit.willRemove', { defaultValue: 'Will be removed' })
                    : t('chat.partEdit.replaceWith', { defaultValue: 'Replace with' })}
                </label>
                <input
                  id={`part-edit-${idx}`}
                ref={idx === 0 ? firstRef : undefined}
                value={values[idx]}
                disabled={removed[idx]}
                onChange={e => handleChange(idx, e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handleSubmit(); }}
                placeholder={t('chat.partEdit.placeholder', { defaultValue: 'e.g. Rear bumper' })}
                style={{
                  width: '100%',
                  padding: '8px 10px',
                  fontSize: '12px',
                  color: removed[idx] ? '#aaa' : '#201e1a',
                  backgroundColor: removed[idx] ? '#f7f6f3' : '#ffffff',
                  border: '1px solid #e5e3df',
                  borderRadius: '7px',
                  outline: 'none',
                  boxSizing: 'border-box',
                  fontFamily: 'inherit',
                  textDecoration: removed[idx] ? 'line-through' : 'none',
                }}
                onFocus={e => { e.currentTarget.style.borderColor = '#6866ff'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(104,102,255,0.12)'; }}
                onBlur={e => { e.currentTarget.style.borderColor = '#e5e3df'; e.currentTarget.style.boxShadow = 'none'; }}
                />
              </div>
              <button
                type="button"
                onClick={() => toggleRemoved(idx)}
                aria-pressed={removed[idx]}
                aria-label={removed[idx]
                  ? t('chat.partEdit.keep', { defaultValue: 'Keep this part' })
                  : t('chat.partEdit.remove', { defaultValue: 'Remove this part' })}
                title={removed[idx]
                  ? t('chat.partEdit.keep', { defaultValue: 'Keep this part' })
                  : t('chat.partEdit.remove', { defaultValue: 'Remove this part' })}
                style={{ alignSelf: 'end', marginBottom: '2px', width: '28px', height: '28px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', border: 'none', borderRadius: '7px', background: 'transparent', color: removed[idx] ? '#6866ff' : '#9a978f', cursor: 'pointer' }}
              >
                {removed[idx] ? <Undo2 size={14} /> : <Trash2 size={14} />}
              </button>
            </div>
          ))}
          {added.map((value, idx) => (
            <div key={`add-${idx}`} data-testid="part-add-row" style={{
              display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 28px',
              gap: '8px', alignItems: 'center', padding: '8px',
              border: '1px dashed #c7c6ff', borderRadius: '10px', backgroundColor: '#f7f6ff',
            }}>
              <div>
                <label htmlFor={`part-add-${idx}`} style={{ fontSize: '9px', color: '#6866ff', display: 'block', marginBottom: '3px', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 650 }}>
                  {t('chat.partEdit.newPart', { defaultValue: 'New part' })}
                </label>
                <input
                  id={`part-add-${idx}`}
                  ref={idx === added.length - 1 ? addRef : undefined}
                  value={value}
                  onChange={e => handleAddedChange(idx, e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handleSubmit(); }}
                  placeholder={t('chat.partEdit.addPlaceholder', { defaultValue: 'e.g. Front left door handle' })}
                  style={{ width: '100%', padding: '8px 10px', fontSize: '12px', color: '#201e1a', backgroundColor: '#ffffff', border: '1px solid #e5e3df', borderRadius: '7px', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' }}
                  onFocus={e => { e.currentTarget.style.borderColor = '#6866ff'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(104,102,255,0.12)'; }}
                  onBlur={e => { e.currentTarget.style.borderColor = '#e5e3df'; e.currentTarget.style.boxShadow = 'none'; }}
                />
              </div>
              <button type="button" onClick={() => dropAdded(idx)}
                aria-label={t('common.remove', { defaultValue: 'Remove' })}
                style={{ alignSelf: 'end', marginBottom: '2px', width: '28px', height: '28px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', border: 'none', borderRadius: '7px', background: 'transparent', color: '#9a978f', cursor: 'pointer' }}>
                <X size={14} />
              </button>
            </div>
          ))}
          <button
            type="button"
            data-testid="part-edit-add"
            onClick={addRow}
            style={{ alignSelf: 'flex-start', display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '6px 10px', fontSize: '12px', fontWeight: 600, color: '#6866ff', background: 'transparent', border: '1px dashed #c7c6ff', borderRadius: '8px', cursor: 'pointer', fontFamily: 'inherit' }}
          >
            <Plus size={13} />
            {t('chat.partEdit.addPart', { defaultValue: 'Add a part' })}
          </button>
        </div>

        {/* Footer */}
        {/* Buttons sit centred in their own band under the divider (they
            used to touch it: no top padding). Same height, same radius. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'flex-end', padding: '12px 16px', flexShrink: 0, borderTop: '1px solid #f0ede9' }}>
          <button
            onClick={onClose}
            style={{ height: '32px', padding: '0 14px', fontSize: '12px', fontWeight: 600, color: '#615e58', backgroundColor: '#ffffff', border: '1px solid #e5e3df', borderRadius: '8px', cursor: 'pointer', fontFamily: 'inherit' }}
            onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#f7f6f3')}
            onMouseLeave={e => (e.currentTarget.style.backgroundColor = '#ffffff')}
          >
            {t('common.cancel', { defaultValue: 'Cancel' })}
          </button>
          <button
            onClick={handleSubmit}
            disabled={!hasChanges}
            style={{
              height: '32px', padding: '0 14px', fontSize: '12px', fontWeight: 600, fontFamily: 'inherit',
              color: '#ffffff',
              backgroundColor: hasChanges ? '#6866ff' : '#c5c4ff',
              border: '1px solid transparent', borderRadius: '8px',
              cursor: hasChanges ? 'pointer' : 'default',
              display: 'inline-flex', alignItems: 'center', gap: '5px',
              transition: 'background-color 0.15s',
            }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
            </svg>
            {t('chat.partEdit.submit', { defaultValue: 'Update search' })}
          </button>
        </div>
      </div>
    </div>
  , document.body);
};

// ── Resolved part header ─────────────────────────────────────────────────────
// Builds the display label + warning-capped probability shown in the part card
// header. Shared with PartsTrackerPanel so the tracker rows always render the
// exact same name and percentage as the result cards.
export function buildResolvedPartHeader(
  partResult: {
    related_corrected_name?: string | null;
    related_user_query?: string | null;
    resolution_keys?: string[] | null;
    part_parameters?: string[] | null;
    resolutions?: string[] | null;
    resolutions_explanation?: string[] | null;
    probability_score?: number | null;
    part_name?: string | null;
    structured_data?: any;
  },
  fallbackName = '',
): { label: string; hasWarnings: boolean; probability: number | null } {
  const resolutions = partResult.resolutions ?? [];
  const hasWarnings = resolutions.length > 0 ||
    (partResult.resolutions_explanation != null && partResult.resolutions_explanation.length > 0);

  // Build display name:
  //   "related_corrected_name, <part_parameters values>, <extra resolution_keys values>"
  //
  // part_parameters carry the qualifiers the user explicitly stated (copied
  // verbatim from the matching ValidationPartItem); resolution_keys carry the
  // qualifiers the search agent picked when disambiguating. We render
  // part_parameters first so user intent leads, then append any resolution_keys
  // values that aren't already covered (dedup is case-insensitive on the value
  // portion after the optional "<key>:" prefix).
  let _cn = partResult.related_corrected_name || partResult.related_user_query || fallbackName;

  // A bare part-number request ("SKU 10588708") is an identifier, not a name —
  // once resolved, lead with the loaded card's own catalog description (works
  // for every catalog impl) and keep the number as the parenthetical reference.
  let numberRef = '';
  const catalogName = String(
    partResult.part_name
    || partResult.structured_data?.part_data?.description
    || partResult.structured_data?.PART_DATA?.DESCRIPTION
    || '',
  ).trim();
  if (isPartNumberQuery(_cn) && catalogName && !isPartNumberQuery(catalogName)) {
    numberRef = _cn;
    _cn = catalogName;
  }

  // If corrected_name contains commas, extract the base name and treat the rest as qualifiers
  let baseNameFromCorrected = _cn;
  let extractedParams: string[] = [];
  if (_cn && _cn.includes(',')) {
    const parts = _cn.split(',').map(p => p.trim());
    baseNameFromCorrected = parts[0]; // First part is the base name
    extractedParams = parts.slice(1); // Rest are qualifiers
  }

  const correctedName = baseNameFromCorrected ? baseNameFromCorrected.charAt(0).toUpperCase() + baseNameFromCorrected.slice(1) : '';

  const titleCaseValue = (raw: string): string => {
    const sepIdx = raw.indexOf(':');
    const val = sepIdx !== -1 ? raw.slice(sepIdx + 1).trim() : raw.trim();
    return val ? val.charAt(0).toUpperCase() + val.slice(1) : '';
  };

  const seenValues = new Set<string>();
  const orderedValues: string[] = [];
  const pushUnique = (raw: string) => {
    const val = titleCaseValue(raw);
    if (!val) return;
    const key = val.toLowerCase();
    if (seenValues.has(key)) return;
    seenValues.add(key);
    orderedValues.push(val);
  };

  // Seed the base name's own words so a qualifier that merely repeats one
  // (e.g. base "Rear Light" + position:Rear) isn't appended again as a
  // duplicate — the label would otherwise read "Rear Light, Rear, Left, Outer".
  // Keep it in the name; drop the redundant qualifier.
  correctedName.toLowerCase().split(/[^a-z0-9]+/).filter(Boolean).forEach(w => seenValues.add(w));

  // Add extracted qualifiers from the corrected name first
  extractedParams.forEach(pushUnique);

  // Handle part_parameters as either array or object (dict)
  const paramsArray = partResult.part_parameters
    ? (Array.isArray(partResult.part_parameters)
        ? partResult.part_parameters
        : Object.values(partResult.part_parameters as Record<string, string>))
    : [];
  paramsArray.filter(Boolean).forEach(pushUnique);

  (partResult.resolution_keys ?? []).filter(Boolean).forEach(pushUnique);

  const labelCore = orderedValues.length > 0
    ? `${correctedName}, ${orderedValues.join(', ')}`
    : correctedName;

  // Append the ORIGINAL user term as a reference (mirrors buildPartDisplayName),
  // so a FOUND part still shows what the user originally asked for — every part
  // references its origin, not just unsearched ones. Only when it adds info
  // (differs from the corrected base name).
  const originalRef = (partResult.related_user_query || numberRef || '').trim();
  const label = (originalRef && originalRef.toLowerCase() !== correctedName.toLowerCase())
    ? `${labelCore} (${originalRef})`
    : labelCore;

  // The backend score is the ONE number: already calibrated for any assumption
  // the agent made and capped server-side on validation warnings
  // (context_state SINGLE/MULTI_WARNING_PROBABILITY_CAP). No client ceiling —
  // the old 0.89 clamp on explained parts made the table say 89% while the
  // diagram panel (raw score) said 95% for the same row.
  const probability = partResult.probability_score != null ? Number(partResult.probability_score) : null;

  return { label, hasWarnings, probability };
}

export function diagramDisplayProbability(
  partResult: Parameters<typeof buildResolvedPartHeader>[0],
): number | null {
  return buildResolvedPartHeader(partResult).probability;
}

// splitPartAndRef moved to utils/partLabel.ts (so non-component modules like
// OrderExportService can use it); re-exported here for existing importers
// (CartSidePanel, tests).
export { splitPartAndRef } from '../../../utils/partLabel';


// ── Assumption Modal ─────────────────────────────────────────────────────────
// Renders assumption resolution strings in a portal-based modal so it is never
// clipped by ancestor overflow:hidden containers (e.g. the part card wrapper).

export const AssumptionTooltip: React.FC<{ lines: string[]; resolutions_explanation?: string[] | null; resolution_keys?: string[] | null }> = ({ lines, resolutions_explanation, resolution_keys }) => {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);

  return (
    <>
      {/* "Needs attention" trigger — identical amber pill to the parts tracker so
          the same condition is marked the same way in both lists. */}
      {/* <button
        onClick={() => setOpen(true)}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: '3px',
          fontSize: '10px', fontWeight: 600, lineHeight: '14px',
          color: '#92400e', backgroundColor: '#fef3c7',
          border: '1px solid #f59e0b', borderRadius: '4px',
          padding: '1px 6px', cursor: 'pointer',
          whiteSpace: 'nowrap', flexShrink: 0, userSelect: 'none',
        }}
        aria-label={t('chat.multiPart.assumptionTriggerLabel', { defaultValue: 'Show search assumptions' })}
        title={t('chat.multiPart.assumptionTriggerTitle', { defaultValue: 'Click to see search assumptions' })}
      >
        <span aria-hidden="true">⚠</span>
        {t('chat.botMessage.needsAttention', { defaultValue: 'Needs attention' })}
      </button> */}

      {/* Portal modal */}
      {open && createPortal(
        <div
          onClick={() => setOpen(false)}
          style={{
            position: 'fixed', inset: 0, zIndex: 99999,
            backgroundColor: 'rgba(0,0,0,0.4)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '20px',
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              width: '100%', maxWidth: '420px',
              backgroundColor: '#ffffff',
              borderRadius: '14px',
              boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
              overflow: 'hidden',
            }}
          >
            {/* Header */}
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '14px 16px 12px 16px',
              borderBottom: '1px solid #f0ede9',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 20 20" fill="#6866ff">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
                <span style={{ fontSize: '13px', fontWeight: 700, color: '#201e1a' }}>
                  {t('chat.multiPart.assumptionModalTitle', { defaultValue: 'Search Results' })}
                </span>
              </div>
              <button
                onClick={() => setOpen(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px', color: '#aaa', display: 'flex', alignItems: 'center' }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>

            {/* Body */}
            <div style={{ padding: '14px 16px 16px 16px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {/* Structured resolution_keys block */}
              {resolution_keys && resolution_keys.length > 0 && (
                <div style={{
                  fontSize: '12px', color: '#201e1a', lineHeight: '20px',
                  backgroundColor: '#fffbeb',
                  border: '1px solid #fde68a',
                  borderRadius: '8px',
                  padding: '10px 12px',
                }}>
                  <span style={{ fontSize: '11px', color: '#92600a', fontWeight: 600 }}>
                    {t('chat.multiPart.assumptionResolvedAs', { defaultValue: 'I chose this part that resolves the warnings as:' })}
                  </span>
                  <div style={{ marginTop: '6px', display: 'flex', flexDirection: 'column', gap: '3px' }}>
                    {resolution_keys.filter(Boolean).map((kv: string, i: number) => {
                      const sepIdx = kv.indexOf(':');
                      const key = sepIdx !== -1 ? kv.slice(0, sepIdx).trim() : kv;
                      const val = sepIdx !== -1 ? kv.slice(sepIdx + 1).trim() : '';
                      const displayKey = key.charAt(0).toUpperCase() + key.slice(1);
                      const displayVal = val.charAt(0).toUpperCase() + val.slice(1);
                      return (
                        <div key={i} style={{ display: 'flex', gap: '4px' }}>
                          <span style={{ fontWeight: 600, color: '#92600a' }}>{displayKey}</span>
                          <span style={{ color: '#666' }}>=</span>
                          <span style={{ color: '#201e1a' }}>{displayVal}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              {lines.map((line, i) => (
                <div key={i} style={{
                  display: 'flex', alignItems: 'flex-start', gap: '10px',
                  backgroundColor: '#fffbeb',
                  border: '1px solid #fde68a',
                  borderRadius: '8px',
                  padding: '10px 12px',
                }}>
                  <span style={{ fontSize: '13px', flexShrink: 0, marginTop: '1px' }}>⚠</span>
                  <span style={{ fontSize: '12px', color: '#201e1a', lineHeight: '18px' }}>{line}</span>
                </div>
              ))}
            </div>

            {/* Footer */}
            <div style={{ padding: '0 16px 14px 16px', display: 'flex', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setOpen(false)}
                style={{
                  padding: '7px 20px', fontSize: '12px', fontWeight: 600,
                  color: '#ffffff', backgroundColor: '#6866ff',
                  border: 'none', borderRadius: '8px', cursor: 'pointer',
                }}
              >
                {t('chat.multiPart.assumptionModalGotIt', { defaultValue: 'Got it' })}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

// ── "Why this score?" — the single source of the reason lines ────────────────
// Used by BOTH surfaces that answer that question: the always-visible panel in
// the Cards view (ScoreReasons) and the badge tooltip in the Table view
// (ProbabilityBadge). They must never disagree — a part whose score is explained
// in one view and bare in the other is worse than one that is bare in both,
// because the user cannot tell whether the reason is missing or the part differs.
//
// Order: the agent's own clinical explanation → its raw resolutions → a synthesis
// from the structured fields that always accompany a reduced score. Returns [] when
// nothing meaningful exists (older sessions), and callers render nothing at all —
// a blank beats the generic "assumptions were made", which tells the user nothing.
export interface ScoreReasonSource {
  resolutions_explanation?: string[] | null;
  resolutions?: string[] | null;
  resolution_keys?: string[] | null;
  alternatives?: Array<{ partId: string; partName: string; reason: string; probability_score?: number | null }> | null;
  fallback_note?: string | null;
  score_capped_dimensions?: string[] | null;
}

export function scoreReasonLines(
  src: ScoreReasonSource,
  t: (key: string, opts?: any) => string,
): string[] {
  const explained = (src.resolutions_explanation?.filter(Boolean) ?? []);
  const lines = explained.length > 0 ? [...explained] : [...(src.resolutions?.filter(Boolean) ?? [])];
  if (lines.length > 0) return lines;

  // Synthesized from the structured fields: which qualifiers were chosen for the
  // user (resolution_keys), how many candidates were weighed (alternatives),
  // whether a fallback search found the part, and whether the backend capped the
  // score over unresolved specification warnings.
  const chosenValues = (src.resolution_keys ?? [])
    .filter(Boolean)
    .map(raw => { const i = raw.indexOf(':'); return (i !== -1 ? raw.slice(i + 1) : raw).trim(); })
    .filter(Boolean);
  if (chosenValues.length > 0) {
    lines.push(t('chat.multiPart.scoreReasonAssumed', {
      defaultValue: 'The catalog offers several variants of this part — "{{values}}" was selected because your request did not specify it.',
      values: chosenValues.join('", "'),
    }));
  }
  if (src.alternatives && src.alternatives.length > 0) {
    lines.push(t('chat.multiPart.scoreReasonAlternatives', {
      defaultValue: 'It was chosen over {{n}} other matching catalog candidate(s) — see the list below.',
      n: src.alternatives.length,
    }));
  }
  if (src.fallback_note) lines.push(src.fallback_note);
  const cappedDims = (src.score_capped_dimensions ?? []).filter(Boolean);
  if (lines.length === 0 && cappedDims.length > 0) {
    lines.push(t('chat.multiPart.scoreReasonCapped', {
      defaultValue: 'Your request did not specify: {{dims}}. Confidence is capped until this is confirmed.',
      dims: cappedDims.join(', '),
    }));
  }
  return lines;
}

// ── Probability badge ─────────────────────────────────────────────────────────
// The coloured confidence pill shown next to the part title. When the score is
// reduced (assumptions were made) or simply low, hovering reveals WHY — sourced
// from resolutions_explanation (clinical, factual reasons) with a fallback to
// the raw resolutions, then a generic note. Addresses the QA gap where a low
// percentage was shown with no explanation.
export const ProbabilityBadge: React.FC<{
  probability: number;
  t: (key: string, opts?: any) => string;
  /** Table rows: the band's initial in a circle, matching the icons beside it.
      The words wrapped the narrow PROB. column onto a second line. */
  compact?: boolean;
}> = ({ probability, t, compact = false }) => {
  // The badge READS as High / Medium / Low (a percentage promised precision the
  // scorer does not have). It no longer opens a "Why this score?" panel of its
  // own: the Expert verdict popup beside it carries the reasoning, and the cards
  // view still spells it out in the always-visible ScoreReasons box.
  const band: MatchBand = matchBand(probability) || 'low';
  const label = t(MATCH_BAND_LABEL[band].key, { defaultValue: MATCH_BAND_LABEL[band].defaultValue });
  // Smooth red (0°) → yellow (60°) → green (120°) via HSL interpolation
  const hue = Math.round(probability * 120);
  const color = `hsl(${hue}, 70%, 30%)`;
  const bg = `hsl(${hue}, 80%, 93%)`;
  return (
    <span
      title={label}
      // Compact is a bare letter, so the band has to reach a screen reader some
      // other way.
      aria-label={compact ? label : undefined}
      style={compact ? {
        fontSize: '11px', fontWeight: 700,
        color, backgroundColor: bg,
        width: '22px', height: '22px', flexShrink: 0,
        borderRadius: '50%',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      } : {
        fontSize: '10px', fontWeight: 700,
        color, backgroundColor: bg,
        borderRadius: '20px',
        padding: '1px 6px', whiteSpace: 'nowrap',
        display: 'inline-flex', alignItems: 'center',
      }}
    >
      {compact ? Array.from(label)[0] : label}
    </span>
  );
};


// ── Always-visible score explanation ──────────────────────────────────────────
// Rendered under the part title whenever the backend supplied evidence for the
// displayed probability. High and low scores need the same transparency; the
// user should not have to infer why 95% is justified any more than why 55% is.
// Reason sources mirror ProbabilityBadge: resolutions_explanation (clinical,
// factual reasons) → raw resolutions → generic assumption note.
export const ScoreReasons: React.FC<{
  probability: number;
  resolutions_explanation?: string[] | null;
  resolutions?: string[] | null;
  resolution_keys?: string[] | null;
  alternatives?: Array<{ partId: string; partName: string; reason: string; probability_score?: number | null }> | null;
  fallback_note?: string | null;
  score_capped_dimensions?: string[] | null;
  t: (key: string, opts?: any) => string;
}> = ({ probability, resolutions_explanation, resolutions, resolution_keys, alternatives, fallback_note, score_capped_dimensions, t }) => {
  const lines = scoreReasonLines(
    { resolutions_explanation, resolutions, resolution_keys, alternatives, fallback_note, score_capped_dimensions },
    t,
  );
  // No meaningful reason available (older sessions, or the agent broke its
  // contract): render nothing.
  if (lines.length === 0) return null;
  const display = lines;

  return (
    <div style={{
      marginTop: '4px',
      padding: '5px 8px',
      backgroundColor: '#fbf7ea',
      border: '1px solid #f0e8c9',
      borderRadius: '7px',
      display: 'flex', flexDirection: 'column', gap: '2px',
    }}>
      <span style={{ fontSize: '10px', fontWeight: 700, color: '#92600a', letterSpacing: '0.02em' }}>
        {t('chat.multiPart.whyThisScore', { defaultValue: 'Why this score?' })}
      </span>
      {display.map((line, i) => (
        <span key={i} style={{ fontSize: '11px', color: '#6e5a23', lineHeight: '15px' }}>
          {line}
        </span>
      ))}
    </div>
  );
};

// ── Alternatives table ────────────────────────────────────────────────────────
// Shows the OTHER catalog candidates the search agent weighed for this component
// but did NOT pick. Populated whenever the agent chose among N >= 2 candidates,
// so the user can see — next to the chosen part and its confidence score — exactly
// what else the catalog offered and why this one was preferred. Collapsed by
// default to keep the card compact; expands to a partName / partId / reason list.
export const AlternativesTable: React.FC<{
  alternatives?: Array<{ partId: string; partName: string; reason: string; probability_score?: number | null }> | null;
  t: (key: string, opts?: any) => string;
}> = ({ alternatives, t }) => {
  if (!alternatives || alternatives.length === 0) return null;
  return (
    <details style={{ marginTop: '2px' }}>
      <summary style={{
        cursor: 'pointer', listStyle: 'none',
        fontSize: '11px', fontWeight: 600, color: '#6866ff',
        display: 'inline-flex', alignItems: 'center', gap: '4px',
        userSelect: 'none',
      }}>
        <span aria-hidden="true" style={{ fontSize: '9px' }}>▾</span>
        {t('chat.multiPart.alternativesToggle', {
          defaultValue: '{{n}} other candidate(s) considered',
          n: alternatives.length,
        })}
      </summary>
      <div style={{
        marginTop: '6px',
        border: '1px solid #ece9f9', borderRadius: '8px', overflow: 'hidden',
      }}>
        {alternatives.map((alt, i) => (
          <div key={`${alt.partId}-${i}`} style={{
            display: 'flex', flexDirection: 'column', gap: '2px',
            padding: '8px 10px',
            borderTop: i === 0 ? 'none' : '1px solid #f2f0fb',
            backgroundColor: i % 2 === 0 ? '#faf9ff' : '#ffffff',
          }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '12px', fontWeight: 600, color: '#201e1a' }}>{alt.partName}</span>
              {alt.partId && (
                <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: '3px', fontSize: '10px', fontFamily: 'monospace' }}>
                  <span style={{ fontWeight: 700, color: '#9b97c4', letterSpacing: '0.03em' }}>
                    {t('chat.multiPart.oeNumber', { defaultValue: 'OE#' })}
                  </span>
                  <span style={{ fontWeight: 600, color: '#201e1a' }}>{displayPartRef(alt.partId)}</span>
                </span>
              )}
            </div>
            {alt.reason && (
              <span style={{ fontSize: '11px', color: '#666', lineHeight: '15px' }}>{alt.reason}</span>
            )}
          </div>
        ))}
      </div>
    </details>
  );
};

// ── Part Assumptions View ─────────────────────────────────────────────────────
// Renders per-part resolution notes produced by the search agent.
// Each part that had validation warnings shows a plain-text note per dimension.
// Now reads resolutions, fallback_attempt, fallback_note, and part_name directly
// from PartResult objects — no fuzzy matching needed.

const PartAssumptionsView: React.FC<{
  parts: PartResult[];
  t: (key: string, opts?: any) => string;
  onSearchPart?: (searchText: string) => void;
  onSendSilent?: (text: string) => void;
  isLoading?: boolean;
  /** When true: no outer border card, no "Selection Notes" header, no part-name row */
  inline?: boolean;
}> = ({ parts, t, onSearchPart, onSendSilent, isLoading, inline }) => {
  const [popupItems, setPopupItems] = React.useState<RefineItem[] | null>(null);
  const canRefine = !isLoading && !!(onSendSilent || onSearchPart);

  const handlePopupSubmit = (items: RefineItem[]) => {
    const msg = buildPartEditMarker(items);
    if (msg.endsWith('%5B%5D')) return;
    if (onSendSilent) onSendSilent(msg);
    else if (onSearchPart) onSearchPart(msg);
  };

  // Only show parts that have resolutions or a fallback
  const visibleParts = parts.filter(
    p => (p.resolutions && p.resolutions.length > 0) || (p.fallback_attempt != null && p.fallback_attempt > 1)
  );

  if (visibleParts.length === 0) return null;

  return (
    <>
      {popupItems !== null && canRefine && (
        <RefinePopup
          items={popupItems}
          t={t}
          onSubmit={handlePopupSubmit}
          onClose={() => setPopupItems(null)}
        />
      )}

      {inline ? (
        /* ── Inline mode: just resolution strings + chips, no card wrapper ── */
        <div style={{ marginTop: '4px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
          {visibleParts.map((part, pIdx) => {
            const resolutions = part.resolutions ?? [];
            return (
              <div key={pIdx}>
                {/* Fallback chip + Refine button */}
                <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '4px', marginBottom: resolutions.length > 0 ? '3px' : '0' }}>
                  {part.fallback_attempt != null && part.fallback_attempt > 1 && (
                    <span
                      title={part.fallback_note || ''}
                      style={{
                        fontSize: '10px', fontWeight: 600,
                        color: '#92600a', backgroundColor: '#fef3c7',
                        border: '1px solid #fde68a', borderRadius: '20px',
                        padding: '1px 6px', whiteSpace: 'nowrap', cursor: part.fallback_note ? 'help' : 'default',
                      }}
                    >
                      {t('chat.multiPart.fallback', { defaultValue: 'Fallback ×{{n}}', n: part.fallback_attempt })}
                    </span>
                  )}
                  {canRefine && resolutions.length > 0 && (
                    <button
                      onClick={() => {
                        const term = String(part.related_user_query || part.part_name || '').trim();
                        if (term) setPopupItems([{ original: term, value: term }]);
                      }}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: '3px',
                        padding: '1px 6px', fontSize: '10px', fontWeight: 600,
                        color: '#6866ff', backgroundColor: 'transparent',
                        border: '1px solid #c5c4ff', borderRadius: '20px',
                        cursor: 'pointer', whiteSpace: 'nowrap',
                        transition: 'background-color 0.15s',
                      }}
                      onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#efedff')}
                      onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="8" height="8" viewBox="0 0 20 20" fill="currentColor">
                        <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                      </svg>
                      {t('chat.multiPart.refine', { defaultValue: 'Refine' })}
                    </button>
                  )}
                </div>
                {/* Resolution strings */}
                {resolutions.map((res, rIdx) => (
                  <div key={rIdx} style={{ display: 'flex', alignItems: 'flex-start', gap: '4px' }}>
                    <span style={{ fontSize: '10px', color: '#92600a', lineHeight: '16px', flexShrink: 0 }}>⚠</span>
                    <span style={{ fontSize: '11px', color: '#666', lineHeight: '16px' }}>{res}</span>
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      ) : (
      <div style={{ marginTop: '8px', borderRadius: '8px', border: '1px solid #e5e3df', overflow: 'hidden', backgroundColor: '#fafaf9' }}>
        {/* Header */}
        <div style={{ padding: '8px 12px', borderBottom: '1px solid #f0ede9' }}>
          <span style={{ fontSize: '11px', fontWeight: 600, color: '#999', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
            {t('chat.multiPart.partAssumptions', { defaultValue: 'Selection Notes' })}
          </span>
        </div>

        {/* Per-part entries */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
          {visibleParts.map((part, pIdx) => {
            const resolutions = part.resolutions ?? [];
            return (
              <div
                key={pIdx}
                style={{
                  padding: '8px 12px',
                  borderBottom: pIdx < visibleParts.length - 1 ? '1px solid #f0ede9' : 'none',
                }}
              >
                {/* Part name + optional fallback chip + refine button */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '6px', marginBottom: resolutions.length > 0 ? '5px' : '0' }}>
                  <span style={{ fontSize: '12px', fontWeight: 600, color: '#201e1a', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {(() => {
                      // Use the shared header builder so this row renders the
                      // EXACT same label as the part card and tracker panel —
                      // including user-stated part_parameters and search-time
                      // resolution_keys, deduped and comma-joined.
                      const fallback = part.related_user_query || part.part_name || '';
                      return buildResolvedPartHeader(part, fallback).label;
                    })()}
                  </span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
                    {part.fallback_attempt != null && part.fallback_attempt > 1 && (
                      <span
                        title={part.fallback_note || ''}
                        style={{
                          fontSize: '10px', fontWeight: 600,
                          color: '#92600a', backgroundColor: '#fef3c7',
                          border: '1px solid #fde68a', borderRadius: '20px',
                          padding: '1px 6px', whiteSpace: 'nowrap', cursor: part.fallback_note ? 'help' : 'default',
                        }}
                      >
                        {t('chat.multiPart.fallback', { defaultValue: 'Fallback ×{{n}}', n: part.fallback_attempt })}
                      </span>
                    )}
                    {canRefine && resolutions.length > 0 && (
                      <button
                        onClick={() => {
                          const term = String(part.related_user_query || part.part_name || '').trim();
                          if (term) setPopupItems([{ original: term, value: term }]);
                        }}
                        style={{
                          display: 'inline-flex', alignItems: 'center', gap: '3px',
                          padding: '1px 6px', fontSize: '10px', fontWeight: 600,
                          color: '#6866ff', backgroundColor: 'transparent',
                          border: '1px solid #c5c4ff', borderRadius: '20px',
                          cursor: 'pointer', whiteSpace: 'nowrap',
                          transition: 'background-color 0.15s',
                        }}
                        onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#efedff')}
                        onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="8" height="8" viewBox="0 0 20 20" fill="currentColor">
                          <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                        </svg>
                        {t('chat.multiPart.refine', { defaultValue: 'Refine' })}
                      </button>
                    )}
                  </div>
                </div>

                {/* Resolution strings — one per ambiguous dimension */}
                {resolutions.length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                    {resolutions.map((res, rIdx) => (
                      <div key={rIdx} style={{ display: 'flex', alignItems: 'flex-start', gap: '6px' }}>
                        <span style={{ fontSize: '11px', color: '#92600a', lineHeight: '16px', flexShrink: 0 }}>⚠</span>
                        <span style={{ fontSize: '11px', color: '#555', lineHeight: '16px' }}>{res}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
      )}
    </>
  );
};

interface MultiPartResultViewProps {
  parts_results: PartResult[];
  messageText: string;
  messageId: string;
  isValidImageData: (imageData: string | null) => boolean;
  onOrderUpdate?: () => void;
  orderUpdateTrigger?: number;
  onSearchPart?: (searchText: string) => void;
  /** Called to send a message programmatically without showing it in the chat input */
  onSendSilent?: (text: string) => void;
  onAddToCart?: (part: any, structuredData: any) => void;
  structuredData?: any;
  isLoading?: boolean;
  requestedParts?: string[];
  notFoundParts?: string[];
  /** Why each not-found term is not found, keyed like `notFoundParts`
   *  (NOT_IN_CATALOG | NO_MATCHING_SECTION | CATALOG_UNAVAILABLE |
   *  SEARCH_INCOMPLETE | PART_LOAD_FAILED). Drives the row's status chip. */
  notFoundReasons?: Record<string, string> | null;
  /** Vector-search hits from the org's embedded data sources, keyed by the
   *  not-found term exactly as it appears in `notFoundParts`. Rendered as a
   *  "Found in your data sources" strip under the matching not-found row. */
  supplierSuggestions?: Record<string, SupplierSuggestion[]> | null;
  /** Vehicle context the backend ranked the suggestions with ("MG ZS"). */
  supplierSuggestionsVehicle?: string | null;
  cartItems?: any[];
  /**
   * Map of OE part numbers (lowercased) → anchor element id that were already shown
   * in earlier messages within the same session. Parts whose number is in this map
   * will be shown with a "brought before" notice and a link to scroll to the original.
   */
  seenPartNumbers?: Map<string, string>;
  /** Parts found in EARLIER bot messages. Rendered (grayed, on demand) below the
   *  current results so this iteration's table can show the full merged list. */
  priorParts?: PartResult[];
  /** The request-line ledger as of THIS message: the whole order, ordered by the
   *  customer's request order, plus the ids this turn was about (ADR 0007). When
   *  present the table reads its rows, numbers and statuses straight off it and
   *  binds cards by `line_id` — no name matching, and nothing live can change a
   *  table that has already rendered. Absent on messages from before the ledger,
   *  which still render through the legacy pairing below. */
  lines?: RequestLine[] | null;
  turnLines?: string[] | null;
  /** True when any message in this conversation carries a real ledger. Then a
   *  message without one, and without cards, is narration and draws no table;
   *  only a conversation from before the ledger derives one per message. */
  sessionHasLedger?: boolean;
  /** Cumulative session validation parts. No longer used for rendering — a card
   *  takes its label from its request line — but still accepted so callers that
   *  pass it keep compiling. */
  validationItems?: SummaryItem[];
  /** True only for the most recent results message. Live cumulative
   *  `validationItems` (which grow as later searches run) drive extra not-found /
   *  history rows ONLY on the latest table — older messages stay frozen to their
   *  own snapshot so a new search updates just the tracker, not past tables. */
  isLatestResults?: boolean;
  /** Notifies the parent which results view is active ('cards' | 'table') so the
   *  message bubble can widen only for the wide table layout. */
  onViewModeChange?: (mode: 'cards' | 'table') => void;
  /** Vision-identified parts linked to the user's own (annotated when available)
   *  photo they were found in — [{part, image_url}]. Matching rows/cards render
   *  a "Your photo" link so the user sees their image in correlation to the part. */
  visionPartImages?: Array<{ part: string; image_url: string }> | null;
  /** True while the deferred complementary round is still expected to deliver
   *  "You might also need" suggestions for this message. Holds the slot open
   *  with a skeleton so the real suggestions cross-fade into reserved space
   *  instead of growing the message under the reader. */
  complementaryPending?: boolean;
  /** Questions skipped this turn (this message's `postponed_questions`). */
  postponedQuestionIds?: string[];
  /** A skipped line's row actions: open its question in the EPC diagram or
   *  the AI chat (the card above the table renders it). */
  onPostponedAction?: (questionId: string, mode: 'diagram' | 'chat') => void;
}

/**
 * The `image_parts` row a result actually resolved to.
 *
 * The callout alone does NOT identify it: several parts routinely share one
 * callout (left vs right at the same location), so `partId` is what picks the
 * right sibling. This lived in three copies with subtly different matching —
 * the translation pass matched on callout only while the render matched on
 * callout + partId, so a left/right pair translated the sibling's description
 * and the rendered name had no entry in the map (it rendered untranslated, with
 * no "Translated" badge, while its neighbours were fine). One matcher now, so
 * the string that is TRANSLATED is always the string that is RENDERED.
 */
export const findHeroImagePart = (partResult: PartResult): any | null => {
  const parts = (partResult as any)?.image_parts;
  if (!Array.isArray(parts) || !partResult?.part_callout_id) return null;
  return parts.find((p: any) =>
    (p.crossCatKey == partResult.part_callout_id ||
      String(p.index) === partResult.part_callout_id ||
      p.calloutLabel === partResult.part_callout_id ||
      String(p.callout) === partResult.part_callout_id ||
      p.part_callout_id === partResult.part_callout_id) &&
    (partResult.partId == (p.partId || p.part_number || p.crossCatKey || ''))
  ) ?? null;
};

/** The OE description a result RENDERS — the exact key every translation lookup
 *  uses (see derivePartView().description). */
export const resultDescription = (partResult: PartResult): string => {
  const hero = findHeroImagePart(partResult);
  return hero?.description || hero?.name || partResult.structured_data?.part_data?.description || '';
};

const supersessionDetails = (heroPart: any, partResult: PartResult) => {
  const heroDetails = heroPart?.extra_details;
  const cardDetails = (partResult as any)?.extra_details;
  return heroDetails?.supersession_chain ? heroDetails
    : cardDetails?.supersession_chain ? cardDetails
    : heroDetails ?? cardDetails ?? null;
};

const MultiPartResultView: React.FC<MultiPartResultViewProps> = ({
  parts_results: parts_results_raw,
  messageText,
  messageId,
  isValidImageData,
  onOrderUpdate,
  orderUpdateTrigger,
  onSearchPart,
  onSendSilent,
  onAddToCart,
  structuredData,
  isLoading,
  requestedParts,
  notFoundParts,
  notFoundReasons,
  supplierSuggestions,
  supplierSuggestionsVehicle,
  cartItems,
  seenPartNumbers,
  priorParts,
  lines,
  turnLines,
  sessionHasLedger = false,
  validationItems,
  isLatestResults = true,
  onViewModeChange,
  visionPartImages,
  complementaryPending,
  postponedQuestionIds,
  onPostponedAction,
}) => {
  const { t, i18n } = useTranslation();
  const { getActiveOrderId, getOrderObject } = useOrder();
  const { currentUser } = useAuth();
  const [resultEditItems, setResultEditItems] = React.useState<RefineItem[] | null>(null);

  const canEditResult = !isLoading && isLatestResults && !!(onSendSilent || onSearchPart);
  // The edit's identity is the LINE as it reads ("Bumper, Rear"), not the
  // customer's raw term: one request ("bumper") can have been split into
  // several lines ("Bumper, Rear, Right" / "Bumper, Rear"), and an edit sent
  // as "bumper" could not be bound to one of them — the untouched sibling
  // lost its found result (live 2026-10-18). The ledger and the validation
  // edit both match a line by its label, exactly. A part-number request
  // keeps the number as its identity.
  const editResult = React.useCallback((requestedTerm: string, label?: string) => {
    const term = requestedTerm.trim();
    const shown = (label || term).trim();
    const identity = isPartNumberQuery(term) ? term : shown;
    if (identity && canEditResult) setResultEditItems([{ original: identity, value: shown, label: shown }]);
  }, [canEditResult]);

  const submitResultEdit = React.useCallback((items: RefineItem[]) => {
    const marker = buildPartEditMarker(items);
    if (marker.endsWith('%5B%5D')) return;
    if (onSendSilent) onSendSilent(marker);
    else onSearchPart?.(marker);
  }, [onSendSilent, onSearchPart]);

  // Per-part link to the user's own photo (vision turns). The vision part name
  // IS the requested term that seeded the search, so match each result's
  // requested/original term against the vision names — fuzzily (partNamesMatch)
  // so validation renames and qualifier decoration still hit.
  const visionImageFor = React.useCallback(
    (...terms: Array<string | null | undefined>): string | null => {
      if (!visionPartImages?.length) return null;
      for (const term of terms) {
        const s = (term || '').trim();
        if (!s) continue;
        for (const vp of visionPartImages) {
          if (vp?.part && vp.image_url && partNamesMatch(s, vp.part)) return vp.image_url;
        }
      }
      return null;
    },
    [visionPartImages],
  );

  // Diagram interactions can still request a part that is not loaded in the
  // current image. Complementary-list clicks no longer use this path.
  const directSearch = React.useCallback(
    (text: string) => {
      if (onSendSilent) onSendSilent(text);
      else onSearchPart?.(text);
    },
    [onSendSilent, onSearchPart],
  );

  // Build a set of duplicate part numbers
  const duplicatePartAnchors = React.useMemo(() => {
    const map = new Map<string, string>(); // partNumber (lower) → anchor id
    if (!seenPartNumbers || seenPartNumbers.size === 0) return map;
    parts_results_raw.forEach((pr) => {
      let pn: string | undefined;
      if (pr.image_parts && Array.isArray(pr.image_parts) && pr.part_callout_id) {
        const hero = pr.image_parts.find((p: any) =>
          p.crossCatKey == pr.part_callout_id ||
          String(p.index) === pr.part_callout_id ||
          p.calloutLabel === pr.part_callout_id ||
          String(p.callout) === pr.part_callout_id ||
          p.part_callout_id === pr.part_callout_id
        );
        pn = hero?.partNumber || hero?.part_number || hero?.crossCatKey;
      }
      if (!pn) pn = pr.structured_data?.part_data?.part_number || pr.structured_data?.part_data?.part_id;
      if (pn) {
        const anchor = seenPartNumbers.get(pn.toLowerCase());
        if (anchor) map.set(pn.toLowerCase(), anchor);
      }
    });
    return map;
  }, [parts_results_raw, seenPartNumbers]);

  const parts_results = parts_results_raw;

  // Helper to create a highlighted image with a red rectangle around the target callout
  const createHighlightedImage = React.useCallback((
    imageData: string,
    callouts: Callout[],
    calloutLabel: string
  ): Promise<string> => {
    return new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          resolve(imageData);
          return;
        }

        canvas.width = img.width;
        canvas.height = img.height;
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

        // Find and highlight the target callout using the same pill-label style as ImageWithCallouts
        const targetCallout = callouts.find(c => c.label === calloutLabel);
        if (targetCallout) {
          let { x, y, w, h } = targetCallout.bounds;
          // Handle normalized coordinates (0-1 range)
          if (targetCallout.normalized || (x <= 1 && y <= 1 && w <= 1 && h <= 1)) {
            x *= canvas.width;
            y *= canvas.height;
            w *= canvas.width;
            h *= canvas.height;
          }

          // Use the recommended (hero) purple pill style matching ImageWithCallouts
          const bgColor = '#7B61FF';
          const textColor = '#FFFFFF';
          const icon = '★';

          const labelPadding = 8;
          const labelHeight = 24;
          const labelText = targetCallout.label;

          ctx.font = 'bold 12px system-ui, -apple-system, sans-serif';
          const textWidth = ctx.measureText(labelText).width;
          const iconWidth = icon ? 16 : 0;
          const labelWidth = textWidth + iconWidth + labelPadding * 2;

          // Position label at the center of the callout bounds
          const labelX = x + w / 2 - labelWidth / 2;
          const labelY = y + h / 2 - labelHeight / 2;

          // Draw pill background
          ctx.fillStyle = bgColor;
          ctx.beginPath();
          ctx.roundRect(labelX, labelY, labelWidth, labelHeight, 12);
          ctx.fill();

          // Draw icon
          ctx.fillStyle = textColor;
          ctx.font = 'bold 14px system-ui, -apple-system, sans-serif';
          ctx.fillText(icon, labelX + labelPadding, labelY + labelHeight / 2 + 5);

          // Draw label text
          ctx.fillStyle = textColor;
          ctx.font = 'bold 12px system-ui, -apple-system, sans-serif';
          ctx.fillText(labelText, labelX + labelPadding + iconWidth, labelY + labelHeight / 2 + 4);
        }

        try {
          resolve(canvas.toDataURL());
        } catch {
          // Canvas tainted (CORS) — fall back to the original URL
          resolve(imageData);
        }
      };
      img.onerror = () => resolve(imageData);
      img.src = imageData;
    });
  }, []);

  // Build a set of part identifiers already in the cart for quick lookup
  const cartPartIdentifiers = React.useMemo(() => {
    const set = new Set<string>();
    cartItems?.forEach((item: any) => {
      const pn = item.structured_data?.part_data?.part_number;
      if (pn) set.add(pn.toLowerCase());
      const desc = item.structured_data?.part_data?.description;
      if (desc) set.add(desc.toLowerCase());
      const pid = item.structured_data?.part_data?.part_id;
      if (pid) set.add(pid.toLowerCase());
    });
    return set;
  }, [cartItems]);

  // In-cart detection keyed STRICTLY on the OE part number — the only globally
  // unique identifier. `cartPartIdentifiers` also carries description + part_id,
  // and part_id is the diagram crossCatKey/callout (e.g. "14"), which is NOT
  // unique across diagrams — matching on it made unrelated parts that share a
  // callout (or a description) wrongly read as "In cart" in the table. Use the
  // OE number for the found-result rows in BOTH the table and card views so they
  // always agree.
  const cartOeNumbers = React.useMemo(() => {
    const set = new Set<string>();
    const add = (v: any) => {
      const k = String(v ?? '').trim().toLowerCase();
      if (k && !NON_IDENTIFYING_REFS.has(k)) set.add(k);
    };
    cartItems?.forEach((item: any) => {
      const pd = item.structured_data?.part_data || {};
      add(pd.part_number);
      add(pd.oe_number);
    });
    return set;
  }, [cartItems]);
  const oeInCart = React.useCallback((...candidates: any[]) => {
    return candidates.some((v) => {
      const k = String(v ?? '').trim().toLowerCase();
      return !!k && !NON_IDENTIFYING_REFS.has(k) && cartOeNumbers.has(k);
    });
  }, [cartOeNumbers]);

  // Batch-translate all part descriptions once when parts or language change
  const [translationsMap, setTranslationsMap] = React.useState<Record<string, string>>({});
  const translationCacheRef = React.useRef<Map<string, Record<string, string>>>(new Map());

  // Build a stable key from all part descriptions + language so we only translate once
  const translationKey = React.useMemo(() => {
    if (!parts_results || parts_results.length === 0) return null;
    const descriptions: string[] = [];
    parts_results.forEach((partResult) => {
      // The description this row will RENDER (hero row via the shared matcher,
      // else the structured fallback) — the key the table looks up.
      const rendered = resultDescription(partResult);
      if (rendered) descriptions.push(rendered);
      // The structured description too, when it differs: other surfaces (cards,
      // cart payloads) still read it.
      const pd = partResult.structured_data?.part_data;
      if (pd?.description && pd.description !== rendered) {
        descriptions.push(pd.description);
      }
    });
    const lang = i18n.language || '';
    return `${lang}::${descriptions.join('||')}`;
  }, [parts_results, i18n.language]);

  React.useEffect(() => {
    if (!translationKey || !parts_results || parts_results.length === 0) {
      setTranslationsMap({});
      return;
    }

    // Check cache
    const cached = translationCacheRef.current.get(translationKey);
    if (cached) {
      setTranslationsMap(cached);
      return;
    }

    let cancelled = false;

    const runBatchTranslation = async () => {
      const items: BatchTranslationItem[] = [];
      const seen = new Set<string>();

      parts_results.forEach((partResult, idx) => {
        // Translate the description this row RENDERS. Matching on the callout
        // alone here (while the render also matched on partId) is exactly how a
        // left/right pair ended up translating the sibling's name and leaving the
        // rendered one untranslated — same matcher on both sides now.
        const desc = resultDescription(partResult);
        if (desc && !seen.has(desc)) {
          seen.add(desc);
          items.push({ key: `part_${idx}_${desc}`, text: desc });
        }
        // The structured description too when it differs — other surfaces (cards,
        // cart payloads) read it, and it costs one short string per part.
        const pdDesc = partResult.structured_data?.part_data?.description || '';
        if (pdDesc && !seen.has(pdDesc)) {
          seen.add(pdDesc);
          items.push({ key: `pd_${idx}_${pdDesc}`, text: pdDesc });
        }

        // extra_details values (side/hand/position + remark sentences) — batch
        // so the attribute chips render in the client language.
        const ed = (partResult as any)?.extra_details;
        if (ed && typeof ed === 'object' && !Array.isArray(ed)) {
          for (const v of Object.values(ed as Record<string, unknown>)) {
            const sv = expandAttribute(String(v ?? '').trim());
            if (sv && !seen.has(sv)) {
              seen.add(sv);
              items.push({ key: `ed_${idx}_${sv}`, text: sv });
            }
          }
        }
      });

      if (items.length === 0) return;

      const batchTranslations = await TranslationBatchManager.request(
        String(getActiveOrderId() ?? ''),
        i18n.language,
        items,
      );

      if (cancelled) return;

      // Build a map from original description text -> translated text
      const map: Record<string, string> = {};
      for (const item of items) {
        const translated = batchTranslations[item.key];
        if (translated) {
          map[item.text] = translated;
        }
      }
      translationCacheRef.current.set(translationKey, map);
      setTranslationsMap(map);
    };

    runBatchTranslation();

    return () => { cancelled = true; };
  }, [translationKey]);

  const [isAddingAll, setIsAddingAll] = React.useState(false);
  const [addingParts, setAddingParts] = React.useState<Set<string>>(new Set());
  // Cards ⇄ Table view toggle. Same result data, two presentations: the rich
  // diagram cards (default) or a compact table (small thumbnail + key details per
  // row) for scanning many parts at once. Declared with the other state hooks so
  // it stays above the early return below (rules-of-hooks).
  // Default to cards on mobile (the wide results table doesn't fit a phone) and
  // the table on desktop. The user can still toggle either way.
  const [viewMode, setViewMode] = React.useState<'cards' | 'table'>(
    () => (typeof window !== 'undefined' && window.innerWidth < 768) ? 'cards' : 'table'
  );
  // Let the parent widen the message bubble only for the wide table layout.
  React.useEffect(() => { onViewModeChange?.(viewMode); }, [viewMode, onViewModeChange]);
  // Clicking a table thumbnail opens that part's REGULAR ImageWithCallouts zoom
  // modal (callouts, zoom, parts list) — identical to the cards/original view.
  // We mount a hidden ImageWithCallouts with autoOpenModal; it portals its own
  // modal and calls onModalClose when dismissed. (ImageWithCallouts handles its
  // own body-scroll lock; we only add Escape-to-close here.)
  const [zoomPart, setZoomPart] = React.useState<{ partResult: PartResult; index: number; focusPartId?: string } | null>(null);
  // Whether the collapsed "full list" (earlier-iteration parts, grayed) is shown.
  const [showHistory, setShowHistory] = React.useState(false);
  // Not-found terms the user ticked, to re-search several at once.
  const [researchSel, setResearchSel] = React.useState<Set<string>>(new Set());
  const toggleResearch = React.useCallback((term: string) => {
    setResearchSel(prev => {
      const next = new Set(prev);
      if (next.has(term)) next.delete(term); else next.add(term);
      return next;
    });
  }, []);
  const runResearch = React.useCallback((terms: string[]) => {
    const q = terms.filter(Boolean).join(', ');
    if (!q) return;
    if (onSearchPart) onSearchPart(q);
    setResearchSel(new Set());
  }, [onSearchPart]);
  React.useEffect(() => {
    if (!zoomPart) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setZoomPart(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [zoomPart]);
  // Table rows the user has expanded to reveal the full detail panel (image,
  // full names, part number/category/price, assumptions). Keyed by uniqueKey.
  // Not-found parts the user has ticked to re-search together.
  const [selectedNotFound, setSelectedNotFound] = React.useState<Set<string>>(new Set());

  const toggleNotFound = React.useCallback((part: string) => {
    setSelectedNotFound(prev => {
      const next = new Set(prev);
      if (next.has(part)) next.delete(part); else next.add(part);
      return next;
    });
  }, []);

  // Fill the chat input with all selected not-found parts as one combined query
  // so the agent re-searches them together (mirrors the single Research button,
  // which also routes through onSearchPart).
  /** Status chip for a not-found row. A catalog fault (CATALOG_UNAVAILABLE /
   *  PART_LOAD_FAILED) is amber "Catalog unavailable" — the catalog never
   *  answered, so red "Not found" would be a false verdict. NO_MATCHING_SECTION
   *  and SEARCH_INCOMPLETE are amber too (the search didn't complete for this
   *  term); only NOT_IN_CATALOG (or no reason) is the red "Not found". */
  const notFoundChip = React.useCallback((term: string, line?: RequestLine): { label: string; title: string; color: string; bg: string; border: string; amber: boolean } => {
    // A ledger line carries its own outcome, so the chip never has to be
    // inferred from a term-keyed lookup that a re-wording can miss.
    if (line) {
      const chip = lineChip(line);
      const palette = {
        ok:          { color: '#137f67', bg: '#e6f5f1', border: '#137f67', amber: false },
        searching:   { color: '#6866ff', bg: '#efedff', border: '#6866ff', amber: false },
        waiting:     { color: '#6866ff', bg: '#efedff', border: '#6866ff', amber: false },
        unconfirmed: { color: '#92400e', bg: '#FEF3C7', border: '#f59e0b', amber: true },
        fault:       { color: '#92400e', bg: '#FEF3C7', border: '#f59e0b', amber: true },
        missing:     { color: '#b3261e', bg: '#fbe4e2', border: '#b3261e', amber: false },
      }[chip.tone];
      return {
        label: t(chip.key, { defaultValue: chip.fallback }),
        title: t(chip.hintKey, { defaultValue: chip.hintFallback }),
        ...palette,
      };
    }
    const reason = notFoundReasons?.[term];
    if (reason === 'CATALOG_UNAVAILABLE' || reason === 'PART_LOAD_FAILED') {
      return {
        label: t('chat.multiPart.reasonCatalogUnavailable', { defaultValue: 'Catalog unavailable' }),
        title: t('chat.multiPart.reasonCatalogUnavailableHint', { defaultValue: "The parts catalog didn't respond for this part — retry it." }),
        color: '#92400e', bg: '#FEF3C7', border: '#f59e0b', amber: true,
      };
    }
    if (reason === 'NO_MATCHING_SECTION') {
      return {
        label: t('chat.multiPart.reasonNeedsDetail', { defaultValue: 'Needs detail' }),
        title: t('chat.multiPart.reasonNeedsDetailHint', { defaultValue: "Couldn't map this to a catalog section — describe where it sits or rephrase." }),
        color: '#92400e', bg: '#FEF3C7', border: '#f59e0b', amber: true,
      };
    }
    if (reason === 'SEARCH_INCOMPLETE') {
      return {
        label: t('chat.multiPart.reasonIncomplete', { defaultValue: 'Search incomplete' }),
        title: t('chat.multiPart.reasonIncompleteHint', { defaultValue: 'The search did not complete for this part — retry it.' }),
        color: '#92400e', bg: '#FEF3C7', border: '#f59e0b', amber: true,
      };
    }
    return {
      label: t('chat.multiPart.noConfirmedMatch', { defaultValue: 'No confirmed match' }),
      title: t('chat.multiPart.noConfirmedMatchHint', { defaultValue: 'This request remains unresolved. A search miss does not prove the part is absent from the catalog.' }),
      color: '#92400e', bg: '#FEF3C7', border: '#f59e0b', amber: true,
    };
  }, [notFoundReasons, t]);


  // Group results that resolve to the SAME requested part. The search agent can
  // return two candidate parts for one request (e.g. two SKUs for "Headlight,
  // Left"); we bucket them by their resolved tracker label so the render loop can
  // wrap such groups in a highlighted "pick one" banner instead of showing two
  // look-alike cards with no explanation. Original indices are preserved so the
  // scroll-anchor ids (`${messageId}-part-${index}`) keep matching the tracker.
  // ── Request-line ledger (ADR 0007) ────────────────────────────────────────
  // When this message carries a ledger, it IS the table: rows, numbers,
  // statuses and card binding all come from it, frozen as of this message. The
  // legacy pairing below stays for messages that predate it.
  const ledger = React.useMemo(() => {
    // Every message has a ledger: the backend's when it sent one, otherwise one
    // derived from the message's own cards and misses. One render path, so a
    // row can never come from guessing which result answers which request.
    const derived = ledgerFromMessage(
      {
        lines, turn_lines: turnLines, parts_results,
        not_found_parts: notFoundParts, not_found_reasons: notFoundReasons,
        requested_parts: requestedParts,
      },
      (pr: PartResult) => positionChipsFromExtra((pr as any).extra_details),
      !sessionHasLedger,
    );
    return buildLedgerRows({
      lines: derived.lines,
      turnLines: derived.turnLines,
      cards: parts_results,
      priorCards: priorParts,
      bindings: derived.bindings,
    });
  }, [lines, turnLines, parts_results, priorParts, notFoundParts, notFoundReasons, requestedParts, sessionHasLedger]);

  /** The request line each of this message's cards answers. */
  const lineByCard = React.useMemo(() => {
    const map = new Map<number, RequestLine>();
    for (const row of ledger.rows) {
      if (row.kind !== 'found') continue;
      for (const i of row.group.indices) map.set(i, row.line);
    }
    return map;
  }, [ledger]);

  /** The label a card shows: its line's, so the card, the table row and the
   *  tracker row are the same string by construction. */
  const labelForCard = React.useCallback(
    (partResult: PartResult, fallback: string) => {
      const index = parts_results.indexOf(partResult);
      return lineByCard.get(index)?.label || fallback;
    },
    [lineByCard, parts_results],
  );

  const partGroups = React.useMemo(
    // One group per found line, cards bound to it. Several cards on one line is
    // the "N options" case.
    () => ledger.rows
      .filter((r): r is Extract<LedgerRow, { kind: 'found' }> => r.kind === 'found')
      .map(r => ({ key: r.group.key, label: r.group.label, indices: r.group.indices })),
    [ledger],
  );




  // The found lines, already in the customer's request order, already numbered,
  // already bound to their cards. Nothing to pair.
  const orderedGroups = React.useMemo(
    () => ledger.rows
      .filter((r): r is Extract<LedgerRow, { kind: 'found' }> => r.kind === 'found')
      .map((r, gi) => ({
        key: r.group.key, label: r.group.label, indices: r.group.indices,
        gi, num: r.num as number | null, line: r.line as RequestLine | undefined,
      })),
    [ledger],
  );

  // The lines a search failed to settle — the rows that offer a re-search, and
  // the terms the data-source lookup is run for.
  const effectiveNotFound = React.useMemo(() => researchableTerms(ledger.rows), [ledger]);

  const handleSearchSelectedNotFound = React.useCallback(() => {
    if (!onSearchPart) return;
    // The rendered list is what the customer ticked, so re-search reads from
    // the same list — under the ledger those terms are the lines a search
    // failed to settle, which is not the raw not_found_parts wire field.
    const chosen = effectiveNotFound.filter(p => selectedNotFound.has(p));
    if (chosen.length === 0) return;
    onSearchPart(chosen.join(', '));
    setSelectedNotFound(new Set());
  }, [onSearchPart, effectiveNotFound, selectedNotFound]);


  // Collect all items that can be added to the cart
  const allAddableItems = React.useMemo(() => {
    if (!parts_results || parts_results.length === 0) return [];
    const items: Array<{ part: any; sd: any; imageCallouts?: Callout[] | null; partCalloutId?: string | null; rawImage?: string }> = [];

    parts_results.forEach((partResult) => {
      // Try to get the hero/recommended part from image_parts using part_callout_id
      let heroPart: any = null;
      if (partResult.image_parts && Array.isArray(partResult.image_parts) && partResult.part_callout_id) {
        heroPart = partResult.image_parts.find((p: any) =>
          p.crossCatKey == partResult.part_callout_id ||
          String(p.index) === partResult.part_callout_id ||
          p.calloutLabel === partResult.part_callout_id ||
          String(p.callout) === partResult.part_callout_id ||
          p.part_callout_id === partResult.part_callout_id
        );
      }

      if (heroPart) {
        const partNumber = heroPart.partNumber || heroPart.part_number || '';
        const partId = heroPart.crossCatKey || partNumber;
        const identifier = (partNumber || heroPart.description || heroPart.name || partId || '').toLowerCase();
        if (identifier && !cartPartIdentifiers.has(identifier) && !cartPartIdentifiers.has(partId?.toString().toLowerCase())) {
          items.push({
            part: {
              partNumber: partNumber,
              partId: partId,
              description: heroPart.description || heroPart.name,
              price: heroPart.price || heroPart.cost,
              image: partResult.image,
              related_user_query: partResult.related_user_query,
            },
            sd: structuredData,
            imageCallouts: partResult.image_callout,
            partCalloutId: partResult.part_callout_id,
            rawImage: partResult.image,
          });
        }
      } else {
        // Fallback: use structured_data.part_data if available
        const pd = partResult.structured_data?.part_data;
        if (pd) {
          const identifier = (pd.part_number || pd.description || pd.part_id || '').toLowerCase();
          if (identifier && !cartPartIdentifiers.has(identifier)) {
            items.push({
              part: {
                partNumber: pd.part_number,
                partId: pd.part_id,
                description: pd.description,
                price: pd.price,
                image: partResult.image,
                related_user_query: partResult.related_user_query,
              },
              sd: structuredData,
              imageCallouts: partResult.image_callout,
              partCalloutId: partResult.part_callout_id,
              rawImage: partResult.image,
            });
          }
        }
      }
    });

    // Not-found parts (only effectiveNotFound)
    effectiveNotFound.forEach((part) => {
      if (!cartPartIdentifiers.has(part.toLowerCase())) {
        items.push({
          part: { partNumber: part, description: part, not_found: true },
          sd: structuredData,
        });
      }
    });

    return items;
  }, [parts_results, effectiveNotFound, cartPartIdentifiers, structuredData]);

  const handleAddAllToCart = React.useCallback(async () => {
    if (!onAddToCart || allAddableItems.length === 0 || isAddingAll) return;
    setIsAddingAll(true);
    try {
      for (const { part, imageCallouts, partCalloutId: calloutId, rawImage } of allAddableItems) {
        // Create highlighted image if callout data is available (same as ImageWithCallouts.addToCart)
        let imageToUse = part.image;
        if (rawImage && imageCallouts && Array.isArray(imageCallouts) && calloutId) {
          const matchingCallout = imageCallouts.find(c => c.label === calloutId);
          if (matchingCallout) {
            imageToUse = await createHighlightedImage(rawImage, imageCallouts, calloutId);
          }
        }

        // Find the matching partResult to resolve heroPart data (mirrors ImageWithCallouts.addToCart)
        const matchingPartResult = parts_results.find(pr => {
          if (!pr.image_parts || !pr.part_callout_id) return false;
          return pr.image_parts.some((p: any) =>
            p.crossCatKey == pr.part_callout_id ||
            String(p.index) === pr.part_callout_id ||
            p.calloutLabel === pr.part_callout_id ||
            String(p.callout) === pr.part_callout_id ||
            p.part_callout_id === pr.part_callout_id
          ) && pr.part_callout_id === calloutId;
        }) ?? parts_results.find(pr =>
          (pr.structured_data?.part_data?.part_number || pr.structured_data?.part_data?.part_id || '') === (part.partNumber || part.partId || '')
        );

        const heroPart = matchingPartResult?.image_parts?.find((p: any) =>
          p.crossCatKey == matchingPartResult.part_callout_id ||
          String(p.index) === matchingPartResult.part_callout_id ||
          p.calloutLabel === matchingPartResult.part_callout_id ||
          String(p.callout) === matchingPartResult.part_callout_id ||
          p.part_callout_id === matchingPartResult.part_callout_id
        ) ?? null;

        const pd = matchingPartResult?.structured_data?.part_data;

        // Build vin_data the same way ImageWithCallouts.addToCart does
        const safeVinData = matchingPartResult?.structured_data?.vin_data ?? {
          make: '', manufacturer: '', model: '', trim: '', vin: '', year: '',
        };

        // Build per-part structuredData (mirrors ImageWithCallouts.addToCart structuredData)
        // Compute resolvedLabel for this part using the SAME resolution as the
        // render loop (matching tracker row → header builder) so cart entries
        // store the exact label shown on the part card and the tracker.
        const addAllResolvedLabel = matchingPartResult
          ? labelForCard(
              matchingPartResult,
              buildResolvedPartHeader(matchingPartResult, part.related_user_query || '').label,
            )
          : (() => {
              const _cn = part.related_user_query || '';
              return _cn ? _cn.charAt(0).toUpperCase() + _cn.slice(1) : '';
            })();

        const partStructuredData = {
          vin_data: safeVinData,
          part_data: {
            // OE part number FIRST — crossCatKey is the diagram callout, shared
            // by left/right pairs, so keying on it collapses the pair into one
            // cart line (see handleAddRowToCart).
            part_id: String(heroPart?.partNumber ?? heroPart?.part_number ?? pd?.part_number ?? part.partNumber ?? heroPart?.crossCatKey ?? pd?.part_id ?? part.partId ?? calloutId ?? ''),
            part_number: String(heroPart?.partNumber ?? heroPart?.part_number ?? pd?.part_number ?? 'N/A'),
            description: String(heroPart?.description ?? heroPart?.name ?? pd?.description ?? part.description ?? ''),
            price: heroPart?.pricing_data ?? pd?.pricing_data ?? String(heroPart?.price_data ?? '') ?? '--',
            // Cart "search" title = the ORIGINAL term the user typed (the
            // parenthetical in the requested-parts list, e.g. "Rear bumper
            // molding"), NOT the full resolved label — keep them in sync with the
            // card's parenthetical (related_user_query).
            query: String(matchingPartResult?.related_user_query ?? part.related_user_query ?? heroPart?.related_user_query ?? ''),
          },
          selection_reason: String(heroPart?.description ?? heroPart?.name ?? pd?.description ?? 'Selected from multi-part results'),
        };

        // Build rawPart with the same shape as ImageWithCallouts.addToCart's rawPart
        const rawPart = {
          partNumber: String(heroPart?.partNumber ?? heroPart?.part_number ?? pd?.part_number ?? part.partNumber ?? ''),
          partId: String(heroPart?.partNumber ?? heroPart?.part_number ?? pd?.part_number ?? part.partNumber ?? heroPart?.crossCatKey ?? pd?.part_id ?? part.partId ?? calloutId ?? ''),
          description: String(heroPart?.description ?? heroPart?.name ?? pd?.description ?? part.description ?? ''),
          price: heroPart?.price ?? heroPart?.cost ?? pd?.price ?? null,
          image: imageToUse,
          related_user_query: addAllResolvedLabel || (heroPart?.related_corrected_name ?? matchingPartResult?.related_corrected_name ?? matchingPartResult?.related_user_query ?? null),
          translated_description: translationsMap[heroPart?.description ?? heroPart?.name ?? pd?.description ?? part.description ?? ''] || null,
          structured_data: heroPart?.structured_data ?? pd ?? null,
          quantity: heroPart?.quantity ?? (matchingPartResult as any)?.quantity ?? (part as any).quantity,
        };

        try {
          await onAddToCart(rawPart, partStructuredData);
        } catch (err) {
          console.error('[AddAll] Failed to add part to cart:', rawPart.partNumber || rawPart.description, err);
        }
      }
    } finally {
      setIsAddingAll(false);
    }
  }, [onAddToCart, allAddableItems, isAddingAll, createHighlightedImage, translationsMap, parts_results]);

  // Every requested part as ONE ordered table row — found results, plus the
  // parts the tracker flags "not in catalog" (validation status not_in_oe) and
  // the searched-but-not-found terms — interleaved by their stable rank so the
  // table reads 1→N exactly like the tracker (a not-in-catalog part keeps its
  // number in place instead of leaving a hole).
  type FoundGroup = (typeof orderedGroups)[number];
  // The ledger already decided which lines this message is about, in which
  // order, with what outcome. Rendering is a read.
  const tableRows = React.useMemo(() => ledger.rows, [ledger]);

  // Supplier suggestions (vector search over org data sources), looked up by the
  // rendered not-found row term. Backend keys are the full not_found_parts terms
  // ("Name (original)") while rows render `splitPartAndRef(term).ref || term`,
  // so register every variant of each key.
  const suggestionsByTerm = React.useMemo(() => {
    const map = new Map<string, SupplierSuggestion[]>();
    for (const [key, hits] of Object.entries(supplierSuggestions ?? {})) {
      if (!hits?.length) continue;
      const { partName, ref } = splitPartAndRef(key);
      for (const variant of [key, ref, partName]) {
        const norm = (variant || '').trim().toLowerCase();
        if (norm && !map.has(norm)) map.set(norm, hits);
      }
    }
    return map;
  }, [supplierSuggestions]);
  // Suggestions fetched client-side for terms the payload carried none for
  // (keyed by normalized term; empty array = queried, nothing found).
  const [autoSuggestions, setAutoSuggestions] = React.useState<Record<string, SupplierSuggestion[]>>({});
  // Vehicle the auto-fetch endpoint reported it ranked with (payload-attached
  // suggestions carry theirs in supplierSuggestionsVehicle instead).
  const [autoVehicle, setAutoVehicle] = React.useState<string>('');
  // What the "matched for your vehicle" hint shows, whichever path supplied it.
  const suggestionsVehicle = (supplierSuggestionsVehicle || '').trim() || autoVehicle;

  const suggestionsFor = React.useCallback(
    (term: string): SupplierSuggestion[] => {
      const norm = (term || '').trim().toLowerCase();
      const hits = suggestionsByTerm.get(norm) ?? autoSuggestions[norm] ?? [];
      // Strong best match (≥80%) → the tail is noise; show only the top 3.
      return trimSuggestionsForDisplay(hits);
    },
    [suggestionsByTerm, autoSuggestions],
  );

  // Payload suggestion keys no rendered row covers — e.g. a not_in_oe term
  // ("Engine Oil") that is absent from requested_parts, not_found_parts AND
  // the found rows: its 8 hits sat invisible in the payload (live, s1).
  // These render as standalone strips in both views.
  const leftoverSuggestionTerms = React.useMemo(() => {
    const covered = new Set<string>();
    const fold = (t: string) => (t || '').trim().toLowerCase();
    (notFoundParts ?? []).forEach(t => {
      covered.add(fold(t));
      covered.add(fold(splitPartAndRef(t).ref || t));
    });
    (requestedParts ?? []).forEach(t => covered.add(fold(t)));
    // NOTE: validationItems (not_in_oe rows) are NOT counted as covered —
    // they only render in the table view; the table dedupes at render time.
    orderedGroups.forEach(g => covered.add(fold(g.label || '')));
    return Object.keys(supplierSuggestions ?? {}).filter(k => {
      const { partName, ref } = splitPartAndRef(k);
      return ![k, ref, partName].some(v => covered.has(fold(v || '')));
    });
  }, [supplierSuggestions, notFoundParts, requestedParts, orderedGroups]);

  // Requested terms this search resolved to NOTHING — no found row AND not in
  // not_found_parts (the tracker's amber "Requires clarification" / no_result
  // state). They get no table row at all, so without this they would never
  // show data-source suggestions either (live feedback: embeddings vanished
  // for exactly such a row).

  // Automatically search the org's embedded data sources for not-found rows
  // whose payload has no suggestions. Latest results only — history messages
  // must not fan out API calls on every reload.
  React.useEffect(() => {
    if (!isLatestResults || dsSearchEnabled === false) return;
    // Not-found terms AND unresolved (no_result) requested terms — both have
    // no catalog answer, so both deserve a data-source lookup. Under the ledger
    // the rendered misses ARE the lines a search failed to settle, so the lookup
    // and the rows it fills in always cover the same terms.
    const missing = [...effectiveNotFound]
      .map(term => (term || '').trim())
      .filter(term => {
        const norm = term.toLowerCase();
        return term && !suggestionsByTerm.has(norm) && !(norm in autoSuggestions);
      });
    if (missing.length === 0) return;
    // Vehicle context ("MG ZS") enriches the embedded query server-side so
    // same-brand price-list rows rank first. Read at fire time (not memoized)
    // — the active order can change between renders.
    const vd = (getOrderObject()?.vin_data ?? {}) as any;
    const vehicle = [
      (vd.make || vd.datasetName || vd.brand || vd.manufacturer || '').trim(),
      (vd.model || vd.modelName || vd.alt_model || '').trim(),
    ].filter(Boolean).join(' ');
    let cancelled = false;
    (async () => {
      for (const term of missing) {
        const norm = term.toLowerCase();
        const cacheKey = `${vehicle.toLowerCase()}|${norm}`;
        let entry = dsSearchCache.get(cacheKey);
        if (entry === undefined) {
          // One request per term: this effect re-runs on every render while a
          // lookup (2–8s) is in flight, and each re-run used to fire the same
          // query again — ~60 identical calls for one not-found row (live).
          let pending = dsSearchInFlight.get(cacheKey);
          if (!pending) {
            pending = chatService.searchDataSources(term, vehicle || undefined)
              .then(res => {
                dsSearchEnabled = !!res.enabled;
                if (!res.enabled) return null;
                const e = { hits: (res.suggestions ?? []) as SupplierSuggestion[], vehicle: (res.vehicle || '').trim() };
                dsSearchCache.set(cacheKey, e);
                return e;
              })
              .catch(() => {
                // Unauthorized / unreachable — stop asking for this runtime.
                dsSearchEnabled = false;
                return null;
              })
              .finally(() => { dsSearchInFlight.delete(cacheKey); });
            dsSearchInFlight.set(cacheKey, pending);
          }
          const got = await pending;
          if (!got) return;
          entry = got;
        }
        if (cancelled) return;
        const resolved = entry;
        if (resolved.vehicle) setAutoVehicle(resolved.vehicle);
        setAutoSuggestions(prev => ({ ...prev, [norm]: resolved.hits }));
      }
    })();
    return () => { cancelled = true; };
  }, [isLatestResults, effectiveNotFound, suggestionsByTerm, autoSuggestions, getOrderObject]);

  const renderCardSuggestions = (suggestions: SupplierSuggestion[], term: string) => (
    <div className="mt-1.5 p-2.5 bg-amber-50/60 border border-amber-200/70 rounded-lg space-y-1.5">
      <div className="flex items-center flex-wrap gap-1.5">
        <span className="text-[10px] font-bold text-amber-900 uppercase tracking-wider">
          {t('chat.multiPart.dataSourceSuggestions', { defaultValue: 'Found in your data sources' })}
        </span>
        {/* The requested term — load-bearing when the not-found row itself is
            hidden because these suggestions replace it. */}
        <span className="text-[10px] font-semibold text-amber-900 bg-white/70 border border-amber-200 rounded-full px-2 py-0.5 normal-case tracking-normal">
          {term}
        </span>
        {suggestionsVehicle && (
          <span
            title={t('chat.multiPart.suggestionsVehicleHint', { defaultValue: 'Suggestions are ranked using your vehicle details' })}
            className="inline-flex items-center gap-1 text-[10px] font-semibold text-amber-800 bg-amber-100/80 border border-amber-200 rounded-full px-2 py-0.5 normal-case tracking-normal"
          >
            <Car size={11} className="shrink-0" />
            {t('chat.multiPart.suggestionsVehicle', { vehicle: suggestionsVehicle, defaultValue: 'Matched for {{vehicle}}' })}
          </span>
        )}
      </div>
      <div className="flex flex-col gap-1.5">
        {suggestions.map(sugg => (
          <div key={sugg.suggestion_id} className="flex items-center justify-between gap-2 text-xs bg-white/80 p-2 rounded border border-amber-100/80 shadow-2xs flex-wrap">
            <div className="flex items-center gap-2 min-w-0 flex-1 flex-wrap">
              <span className="font-semibold text-gray-800">{sugg.name}</span>
              {sugg.part_number && (
                <span className="font-mono text-[10px] text-gray-600 bg-gray-100 rounded px-1.5 py-0.5 shrink-0">
                  {sugg.part_number}
                </span>
              )}
              {sugg.supplier_name && (
                <span className="text-[10px] text-gray-500 shrink-0">{sugg.supplier_name}</span>
              )}
              {sugg.price != null && (
                <span className="font-semibold text-emerald-700 shrink-0">
                  {sugg.price.toLocaleString()}{sugg.currency ? ` ${sugg.currency}` : ''}
                </span>
              )}
              <span
                title={t('chat.multiPart.similarityHint', { defaultValue: 'Semantic similarity to your request' })}
                className="text-[9px] font-semibold text-indigo-600 bg-indigo-50 border border-indigo-100 rounded-full px-1.5 py-0.5 shrink-0"
              >
                ~{Math.max(0, Math.round((1 - sugg.distance) * 100))}%
              </span>
            </div>
            {onAddToCart && (cartPartIdentifiers.has(sugg.suggestion_id.toLowerCase()) ? (
              <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-600 shrink-0">
                <Check size={12} /> {t('chat.multiPart.inCart', { defaultValue: 'In cart' })}
              </span>
            ) : (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); handleAddSuggestionToCart(sugg, term); }}
                disabled={addingParts.has(sugg.suggestion_id)}
                className="inline-flex items-center gap-1 text-xs font-semibold text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 rounded-md px-2 py-1 shrink-0 transition-colors"
                title={t('chat.multiPart.addToCart', { defaultValue: 'Add to Cart' })}
              >
                {addingParts.has(sugg.suggestion_id) ? <Loader2 size={12} className="animate-spin" /> : <ShoppingCart size={12} />}
                {t('chat.multiPart.add', { defaultValue: 'Add' })}
              </button>
            ))}
          </div>
        ))}
      </div>
    </div>
  );

  // Parts found in EARLIER iterations, for the collapsed "full list" view — de-
  // duped against this table's own parts and ranked so they interleave 1→N.
  // The rest of the order, as this message saw it — frozen like the rows above,
  // so a later search cannot rewrite what this table already showed.
  const historyRows = React.useMemo(() => ledger.historyRows as Array<any>, [ledger]);

  // A search that confirmed nothing still has rows to show: every requested line
  // is accounted for in the ledger, and each says what happened to it. Only a
  // message with neither cards nor lines has nothing to render.
  if ((!parts_results || parts_results.length === 0) && !hasSettledOutcome(ledger.rows)) {
    return null;
  }

  // Count distinct REQUESTED parts, not candidate cards: when the search returns
  // multiple options for one request they are grouped into a single partGroup, so
  // this header count lines up with the parts tracker (which is keyed on
  // requested parts too) instead of inflating to the number of result cards.
  const notFoundCount = effectiveNotFound.length;
  const foundCount = ledger ? ledger.turnFound : partGroups.length;
  const totalRequested = ledger ? ledger.turnTotal : partGroups.length + notFoundCount;

  const allInCart = allAddableItems.length === 0 && parts_results.length > 0 && (cartItems?.length ?? 0) > 0;

  // Resolve the display fields for one result the SAME way renderCard does
  // (hero part from image_parts → structured_data fallback), so the table row and
  // the card always agree on label, number, price and cart state.
  const derivePartView = (partResult: PartResult, index: number) => {
    // Shared with the translation pass — see findHeroImagePart.
    const heroPart: any = findHeroImagePart(partResult);
    const pd = partResult.structured_data?.part_data;
    const partTitle = partResult.related_user_query || heroPart?.description || heroPart?.name || pd?.description || '';
    // Display vs IDENTITY. In guest mode the backend masks every OE number to a
    // "ref-xxxxxxxxxx" token and displayPartRef renders it as the literal "•••" —
    // the SAME string for every part. Feeding that display string into cart
    // identity collapsed all guest parts into one: adding a single part made every
    // row read "in cart", and a second add merged into the first line. Identity
    // always uses the RAW value (the token IS unique); "•••" is display only.
    const partNumberRaw = String(heroPart?.partNumber || heroPart?.part_number || pd?.part_number || '');
    const partNumber = displayPartRef(partNumberRaw);
    const category = heroPart?.category || pd?.category || '';
    const oeNumber = displayPartRef(heroPart?.oe_number || pd?.oe_number || pd?.category || '');
    const price = heroPart?.pricing_data || pd?.pricing_data || '';
    const description = heroPart?.description || heroPart?.name || pd?.description || '';
    const { label: headerLabel, hasWarnings, probability } = buildResolvedPartHeader(partResult, partTitle);
    const resolvedLabel = labelForCard(partResult, headerLabel);
    // In-cart by OE part number only (see cartOeNumbers) — never by callout/description.
    const isInCart = oeInCart(partNumberRaw, heroPart?.oe_number, pd?.oe_number, partResult.partId);
    const originalQuery = String(partResult.related_user_query ?? heroPart?.related_user_query ?? '');
    const hasValidImage = !!(partResult.image && isValidImageData(partResult.image));
    // Catalog quantity — the top-level `quantity` the cart line is seeded from
    // (NOT part_data.qty). Defaults to 1 when absent (see parseCatalogQuantity).
    const catalogQty = parseCatalogQuantity(heroPart?.quantity ?? (partResult as any).quantity);
    // Replacement chain — on the diagram row when we have it, else on the card.
    const supersessionRow = { partNumber: partNumberRaw, extra_details: supersessionDetails(heroPart, partResult) };
    return { heroPart, pd, partNumber, partNumberRaw, category, oeNumber, price, description, resolvedLabel, probability, hasWarnings, isInCart, originalQuery, image: partResult.image, hasValidImage, catalogQty, supersessionRow, uniqueKey: `${messageId}-part-${index}` };
  };

  // Distinguishing qualifiers of the resolved catalog part (side / position / etc).
  // When two parts share the SAME OE description (e.g. left vs right "DOOR ASM-RR SI")
  // these are the only thing that tells them apart, so we surface them as chips.
  // part_parameters come as "key:value" (["position:Rear","side:Left"]) or a dict.
  // Keys whose extra_details / part_parameters value is an OE "note" — a remark,
  // a general note, or an optional-package flag — surfaced in the OE Notes column
  // (normalized: lowercased, spaces/underscores/hyphens stripped).
  const OE_NOTE_KEYS = new Set(['remark', 'remarks', 'note', 'notes', 'optionalpack', 'optionalpackage']);
  const partNoteValues = (pr: PartResult): string[] => {
    const entries: [string, string][] = [];
    const ed = (pr as any)?.extra_details;
    if (ed && typeof ed === 'object' && !Array.isArray(ed)) {
      for (const [k, v] of Object.entries(ed as Record<string, unknown>)) entries.push([String(k), String(v ?? '')]);
    }
    const pp = pr?.part_parameters;
    if (pp) {
      const arr = Array.isArray(pp) ? pp : Object.entries(pp as Record<string, string>).map(([k, v]) => `${k}:${v}`);
      for (const p of arr) { const s = String(p ?? ''); const i = s.indexOf(':'); if (i !== -1) entries.push([s.slice(0, i), s.slice(i + 1)]); }
    }
    const out: string[] = [];
    const seen = new Set<string>();
    for (const [rawKey, rawVal] of entries) {
      const key = rawKey.toLowerCase().replace(/[\s_-]/g, '');
      if (!OE_NOTE_KEYS.has(key)) continue;
      const val = rawVal.trim();
      const norm = val.toLowerCase();
      if (!val || ['n/a', 'na', 'none', 'null', '-', '—'].includes(norm) || seen.has(norm)) continue;
      seen.add(norm);
      out.push(val);
    }
    return out;
  };

  // Small "Translated" chip shown next to any value that is a client-language
  // translation (not the original catalog text), so it's clearly marked as such.
  const translatedBadge = (
    <span style={{ flexShrink: 0, fontSize: '9px', fontWeight: 600, color: '#6866ff', backgroundColor: '#efedff', borderRadius: '4px', padding: '0 5px', lineHeight: '14px', whiteSpace: 'nowrap' }}>
      {t('chat.multiPart.translated', { defaultValue: 'Translated' })}
    </span>
  );


  // An OE name cell: the customer's language FIRST, the catalog's own words
  // underneath. The catalog text used to lead — "BUMPER, FRONT Left" on a
  // Spanish session, the Spanish only in small grey below (live 2026-09-29).
  // The original stays visible (it is what the supplier's invoice says).
  const oeWrap: React.CSSProperties = { whiteSpace: 'normal', overflowWrap: 'break-word', wordBreak: 'normal', lineHeight: 1.35 };
  const renderOeName = (orig: string, trans: string) => (
    trans && trans.trim() !== orig.trim() ? (
      <>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: '5px' }}>
          <span style={oeWrap}>{trans}</span>{translatedBadge}
        </div>
        <div style={{ ...oeWrap, fontSize: '11px', fontWeight: 400, color: '#6b6b6b', marginTop: '2px' }}>{orig}</div>
      </>
    ) : <>{orig || '—'}</>
  );

  // "Manual Selection" — this part is the option the USER picked when answering a
  // clarification, not one the resolver chose for them. Sits with the probability,
  // which is the other statement about how this part was arrived at: a score means
  // the resolver decided, this chip means a person did. Green (a confirmation, not
  // a warning) with a check. Only the wide cards header shows it; in the table
  // the PROB. column simply shows no score for such a row (a bare check there
  // read as a verdict, not as provenance).
  const userSelectedBadge = (
    <span
      title={t('chat.multiPart.userSelectedTitle', { defaultValue: 'You chose this part when answering the question' })}
      style={{
        display: 'inline-flex', alignItems: 'flex-start', gap: '3px',
        maxWidth: '100%', minWidth: 0,
        fontSize: '9px', fontWeight: 600, color: '#137f67', backgroundColor: '#c7ffe8',
        borderRadius: '4px', padding: '1px 5px', lineHeight: '12px',
        whiteSpace: 'normal', overflowWrap: 'break-word',
      }}
    >
      <svg xmlns="http://www.w3.org/2000/svg" width="9" height="9" viewBox="0 0 20 20" fill="currentColor" aria-hidden style={{ flexShrink: 0, marginTop: '1px' }}>
        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
      </svg>
      {/* The label needs its OWN flex item with min-width:0. As bare text it is
          an anonymous item with min-width:auto, which refuses to shrink below its
          content — so it overflowed the chip and the cell clipped it mid-word
          ("Selectior"). With min-width:0 it wraps to fit whatever space is left. */}
      <span style={{ minWidth: 0, overflowWrap: 'anywhere' }}>
        {t('chat.multiPart.userSelected', { defaultValue: 'Manual Selection' })}
      </span>
    </span>
  );

  // OE Notes cell — the remark/notes/optional-package values, translated when a
  // batch translation is available (they're batched via extra_details), wrapped.
  // Each value that was actually translated gets a "Translated" chip.
  const renderOeNotes = (pr: PartResult) => {
    const notes = partNoteValues(pr);
    if (notes.length === 0) return <span style={{ fontSize: '11px', color: '#c4bfb6' }}>—</span>;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
        {notes.map((n, i) => {
          const tn = translationsMap[n];
          const isTranslated = !!tn && tn.trim() !== n.trim();
          return (
            // Block + overflow-wrap:anywhere so a long unbroken token
            // ("proveedor:INTEVA,panel…" with no spaces) wraps inside the fixed
            // column instead of bleeding into PROB./ACCIONES. (An inline-flex item
            // has min-width:auto and won't shrink below its content, which caused
            // the overflow.) The "Translated" badge follows the text inline.
            <span key={i} style={{ display: 'block', minWidth: 0, maxWidth: '100%', whiteSpace: 'normal', overflowWrap: 'anywhere', wordBreak: 'break-word', lineHeight: 1.35 }}>
              {tn || n}
              {isTranslated && <>{' '}{translatedBadge}</>}
            </span>
          );
        })}
      </div>
    );
  };

  // Side/position chips (Left/Right, Front/Rear…) pulled from an "L/R"-style
  // extra_details column — the qualifier that tells same-named parts apart when
  // it isn't already carried in part_parameters/the name. Translated via the
  // same batch (extra_details values are expanded + batched above).
  const tPosChip = (word: string): string => {
    const slug = positionChipSlug(word);
    return slug ? String(t(`chat.position.${slug}`, { defaultValue: word })) : word;
  };

  const renderPositionChips = (pr: PartResult) => {
    const positions = positionChipsFromExtra((pr as any)?.extra_details);
    if (positions.length === 0) return null;
    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '3px' }}>
        {positions.map((p, i) => (
          <span key={i} style={{ flexShrink: 0, fontSize: '10px', fontWeight: 600, color: '#3627BF', backgroundColor: '#eef0ff', border: '1px solid #d6d8ff', borderRadius: '6px', padding: '1px 6px', whiteSpace: 'nowrap' }}>
            {tPosChip(p) !== p ? tPosChip(p) : (translationsMap[p] || p)}
          </span>
        ))}
      </div>
    );
  };

  // The part's "from" value (its availability/effective date) — read straight from
  // the part's `from` property: the resolved hero (image_parts), then part_data,
  // then the top-level field. '' when absent/placeholder.
  const partFromValue = (pr: PartResult): string => {
    const p = pr as any;
    let heroFrom = '';
    if (Array.isArray(p?.image_parts) && p?.part_callout_id) {
      const hero = p.image_parts.find((x: any) =>
        (x.crossCatKey == p.part_callout_id || String(x.index) === p.part_callout_id || x.calloutLabel === p.part_callout_id || String(x.callout) === p.part_callout_id || x.part_callout_id === p.part_callout_id) &&
        (p.partId == (x.partId || x.part_number || x.crossCatKey || ''))
      );
      heroFrom = hero?.from || '';
    }
    const val = String(heroFrom || p?.structured_data?.part_data?.from || p?.from || '').trim();
    return ['n/a', 'na', 'none', 'null', '-', '—'].includes(val.toLowerCase()) ? '' : val;
  };

  // Add a single table row's part to the cart — mirrors handleAddSingleToCart's
  // payload exactly (highlighted image, per-part structured_data, query badge) so
  // adding from the table is identical to adding from a card.
  const handleAddRowToCart = async (partResult: PartResult, index: number) => {
    if (!onAddToCart) return;
    const v = derivePartView(partResult, index);
    flushSync(() => { setAddingParts(prev => new Set(prev).add(v.uniqueKey)); });
    try {
      let imageToUse = partResult.image;
      if (partResult.image && partResult.image_callout && Array.isArray(partResult.image_callout) && partResult.part_callout_id) {
        const matchingCallout = partResult.image_callout.find(c => c.label === partResult.part_callout_id);
        if (matchingCallout) imageToUse = await createHighlightedImage(partResult.image, partResult.image_callout, partResult.part_callout_id);
      }
      const perPartStructuredData = {
        vin_data: partResult.structured_data?.vin_data ?? structuredData?.vin_data ?? {
          make: '', manufacturer: '', model: '', trim: '', vin: '', year: '',
        },
        part_data: {
          // Cart identity = OE part number FIRST. crossCatKey is the diagram
          // callout ("1") and is shared by left/right pairs on one diagram —
          // keyed on it, adding the second side merges into the first line's
          // quantity and the part silently never enters the cart.
          part_id: String(v.partNumberRaw || partResult.partId || v.heroPart?.crossCatKey || ''),
          part_number: String(v.partNumberRaw || 'N/A'),
          description: String(v.description || ''),
          price: v.price ?? '--',
          query: v.originalQuery,
        },
        selection_reason: String(v.description || 'Selected from multi-part results'),
      };
      await onAddToCart({
        partNumber: v.partNumberRaw,
        partId: v.partNumberRaw || partResult.partId || v.heroPart?.crossCatKey,
        description: v.description,
        price: v.price,
        image: imageToUse,
        related_user_query: v.originalQuery || v.resolvedLabel || partResult.related_user_query,
        translated_description: translationsMap[v.description] || null,
        quantity: (v.heroPart as any)?.quantity ?? (partResult as any).quantity,
      }, perPartStructuredData);
    } finally {
      setAddingParts(prev => { const next = new Set(prev); next.delete(v.uniqueKey); return next; });
    }
  };

  // Add a datasource suggestion (vector-search hit) to the cart. The line is
  // pre-priced from the data-source row: `pricing_data` rides on the cart item
  // so it renders priced immediately and the backend skips the DMS pricing DAG
  // (part_data.price_source === 'datasource'). Identity is the namespaced
  // suggestion_id (`vs-{source}-{hash}`) — it can never collide with catalog
  // part ids, and re-adding the same row merges into the existing line.
  const handleAddSuggestionToCart = async (sugg: SupplierSuggestion, term: string) => {
    if (!onAddToCart) return;
    flushSync(() => { setAddingParts(prev => new Set(prev).add(sugg.suggestion_id)); });
    try {
      await onAddToCart({
        partId: sugg.suggestion_id,
        partNumber: sugg.part_number || undefined,
        partName: sugg.name,
        description: sugg.name,
        price: sugg.price != null ? String(sugg.price) : undefined,
        price_source: 'datasource',
        supplier_name: sugg.supplier_name || sugg.source_name || undefined,
        currency: sugg.currency || undefined,
        related_user_query: term,
        pricing_data: sugg.price != null ? [{
          price: sugg.price,
          ...(sugg.currency ? { currency: sugg.currency } : {}),
          supplier: sugg.supplier_name || sugg.source_name || undefined,
          source: 'datasource',
        }] : [],
      }, structuredData);
    } finally {
      setAddingParts(prev => { const next = new Set(prev); next.delete(sugg.suggestion_id); return next; });
    }
  };

  // Compact table — columns in fixed order: OE number, OE name, Name (translated,
  // shown only when a translation exists), Search description, Probability,
  // Status (in cart / warning / add to cart). `table-layout: fixed` + the
  // Text cells WRAP (multi-line) so long part names / descriptions are fully
  // readable in place. The table stays horizontally scrollable for the narrow
  // chat column when needed.
  const renderTable = () => {
    // Headers WRAP (whiteSpace: normal) rather than nowrap: with table-layout:fixed
    // a long localized header — e.g. Spanish "DESCRIPCIÓN OE TRADUCIDA" — would
    // otherwise overflow its fixed column and bleed into the neighbouring "PROB."
    // header, rendering as garbled overlapping text.
    const th: React.CSSProperties = { fontSize: '10px', fontWeight: 700, letterSpacing: '0.02em', textTransform: 'uppercase', color: '#9a958c', padding: '8px 6px', textAlign: 'left', whiteSpace: 'normal', overflowWrap: 'break-word', verticalAlign: 'bottom' };
    const td: React.CSSProperties = { padding: '8px 6px', verticalAlign: 'top', borderTop: '1px solid #f0ede9' };
    // Text cells wrap at WORD boundaries (overflowWrap: break-word, not "anywhere")
    // so words stay whole and readable — only over-long unbreakable tokens split.
    const wrap: React.CSSProperties = { whiteSpace: 'normal', overflowWrap: 'break-word', wordBreak: 'normal', lineHeight: 1.35 };
    // "Found in your data sources" strip — vector-search hits rendered under a
    // not-found OR not-in-catalog row (both mean "the OE catalog can't serve
    // this"; the org's own data sources often can). colSpan is computed inside
    // the renderer: showNotes/showFrom are declared further down and are only
    // safe to read at render time (TDZ).
    const renderSuggestionsRow = (suggestions: SupplierSuggestion[], term: string, line?: RequestLine) => (
      <tr>
        <td colSpan={9 + (showNotes ? 1 : 0) + (showFrom ? 1 : 0)} style={{ ...td, padding: '6px 10px 8px 10px', backgroundColor: '#faf9f7' }}>
          <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '6px', marginBottom: '2px' }}>
            <span style={{ fontSize: '10px', fontWeight: 700, color: '#7c6f5a', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
              {t('chat.multiPart.dataSourceSuggestions', { defaultValue: 'Found in your data sources' })}
            </span>
            {/* The requested term — load-bearing when the not-found row itself
                is hidden because these suggestions replace it. */}
            <span style={{ fontSize: '10px', fontWeight: 600, color: '#55524b', backgroundColor: '#ffffff', border: '1px solid #e5e0d6', borderRadius: '20px', padding: '1px 8px' }}>
              {term}
            </span>
            <ExpertVerdictChip line={line} t={t} />
            {suggestionsVehicle && (
              <span
                title={t('chat.multiPart.suggestionsVehicleHint', { defaultValue: 'Suggestions are ranked using your vehicle details' })}
                style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '10px', fontWeight: 600, color: '#55524b', backgroundColor: '#efece6', borderRadius: '20px', padding: '1px 8px' }}
              >
                <Car size={11} style={{ flexShrink: 0 }} />
                {t('chat.multiPart.suggestionsVehicle', { vehicle: suggestionsVehicle, defaultValue: 'Matched for {{vehicle}}' })}
              </span>
            )}
          </div>
          {suggestions.map((sugg, si) => (
            <SupplierSuggestionRow
              key={sugg.suggestion_id}
              sugg={sugg}
              withDivider={si > 0}
              inCart={cartPartIdentifiers.has(sugg.suggestion_id.toLowerCase())}
              adding={addingParts.has(sugg.suggestion_id)}
              onAdd={onAddToCart ? () => handleAddSuggestionToCart(sugg, term) : undefined}
            />
          ))}
        </td>
      </tr>
    );
    // Small inline diagram thumbnail (click to zoom) shown in its own narrow
    // column, so the image is always visible without expanding the row.
    const renderImageCell = (pv: any, pr: PartResult, idx: number) => (
      <td style={{ ...td, padding: '6px 4px', textAlign: 'center' }}>
        {pv.hasValidImage && pv.image ? (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setZoomPart({ partResult: pr, index: idx }); }}
            title={t('chat.multiPart.zoomImage', { defaultValue: 'Click to zoom' })}
            style={{ position: 'relative', width: '38px', height: '38px', borderRadius: '6px', overflow: 'hidden', backgroundColor: '#fff', border: '1px solid #ebe7df', padding: 0, cursor: 'zoom-in', display: 'inline-block', verticalAlign: 'top' }}
          >
            <img src={pv.image} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
            <span style={{ position: 'absolute', bottom: 1, right: 1, display: 'flex', background: 'rgba(255,255,255,0.85)', borderRadius: '3px', padding: '1px' }}>
              <ZoomIn size={9} color="#6866ff" />
            </span>
          </button>
        ) : (
          <div style={{ width: '38px', height: '38px', borderRadius: '6px', backgroundColor: '#faf9f7', border: '1px solid #f0ede9', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', verticalAlign: 'top' }}>
            <ImageOff size={14} color="#c4bfb6" />
          </div>
        )}
      </td>
    );
    // A non-hero candidate row for a multi-candidate group ("N options"): same
    // columns as a found row, minus the request #/term (it shares the lead row's
    // request), marked with a corner arrow so it reads as an alternative to pick.
    // Safety net so the table always surfaces every candidate the tracker counts,
    // even if a clarification was dropped upstream and the group didn't pause.
    const renderAltOptionRow = (altPr: PartResult, altIdx: number) => {
      const av = derivePartView(altPr, altIdx);
      const aOe = av.oeNumber || av.partNumber || '—';
      const aName = av.description || '';
      const aTrans = aName ? translationsMap[aName] : '';
      const aNameTrans = aTrans && aTrans !== aName ? aTrans : '';
      const aAdding = addingParts.has(av.uniqueKey);
      return (
        <tr key={av.uniqueKey} style={{ backgroundColor: '#faf9f7' }}>
          {renderImageCell(av, altPr, altIdx)}
          <td style={{ ...td, textAlign: 'center', padding: '8px 2px', fontSize: '12px', color: '#c4bfb6' }} aria-hidden="true">↳</td>
          <td style={{ ...td, ...wrap, fontSize: '10px', fontWeight: 600, color: '#9a958c', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
            {t('chat.multiPart.otherOptions', { defaultValue: 'Other matching parts' })}
          </td>
          <td style={{ ...td, ...wrap, fontSize: '11px', fontWeight: 700, color: '#201e1a', fontFamily: 'ui-monospace, monospace' }}>{aOe}</td>
          <td style={{ ...td, textAlign: 'center', fontSize: '11px', fontWeight: 600, color: '#201e1a' }}>{av.catalogQty}</td>
          <td style={{ ...td, ...wrap, fontSize: '12px', fontWeight: 600, color: '#201e1a' }}>
            {renderOeName(aName || '', aNameTrans || '')}
            {renderPositionChips(altPr)}
          </td>
          {showNotes && <td style={{ ...td, ...wrap, fontSize: '11px', color: '#6b6b6b' }}>{renderOeNotes(altPr)}</td>}
          {showFrom && <td style={{ ...td, ...wrap, fontSize: '11px', color: '#6b6b6b' }}>{partFromValue(altPr) || '—'}</td>}
                      <td style={{ ...td, textAlign: 'center', overflow: 'hidden' }}><SourceBadge kind="epc" /></td>
          <td style={{ ...td, textAlign: 'center' }}>
            {av.probability != null
              ? <ProbabilityBadge probability={av.probability} t={t} compact />
              : <span style={{ fontSize: '11px', color: '#c4bfb6' }}>—</span>}
          </td>
          <td style={{ ...td, textAlign: 'center' }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'center', gap: '5px' }}>
              {onAddToCart && (av.isInCart ? (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '11px', fontWeight: 600, color: '#137f67' }}>
                  <Check size={13} /> {t('chat.multiPart.inCart', { defaultValue: 'In cart' })}
                </span>
              ) : (
                <button
                  onClick={(e) => { e.stopPropagation(); handleAddRowToCart(altPr, altIdx); }}
                  disabled={aAdding}
                  className="hover:opacity-90 transition-opacity"
                  title={t('chat.multiPart.addToCart', { defaultValue: 'Add to Cart' })}
                  style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '3px', fontSize: '10px', fontWeight: 600, color: '#6866ff', backgroundColor: 'transparent', border: '1px solid #6866ff', borderRadius: '6px', padding: '3px 6px', whiteSpace: 'nowrap', maxWidth: '100%', cursor: aAdding ? 'not-allowed' : 'pointer', opacity: aAdding ? 0.6 : 1 }}
                >
                  {aAdding ? <Loader2 size={13} className="animate-spin" /> : <ShoppingCart size={13} />}
                  {t('chat.multiPart.add', { defaultValue: 'Add' })}
                </button>
              ))}
            </div>
          </td>
        </tr>
      );
    };
    // ONE row shape for a found part, whichever search found it. The rest of
    // the order used to draw its own reduced copy (raw request text, no pencil,
    // no verdict) — an earlier part must read exactly as it did in its search.
    const renderFoundRow = (
      partResult: PartResult,
      index: number,
      num: number | null | undefined,
      groupLine: RequestLine | undefined,
      groupLabel: string,
      options: Array<[PartResult, number]>,
    ) => {
      // Every OTHER candidate the search grouped under this ONE request
      // ("N options"). Normally 0 (rejected candidates ride on the hero's
      // `alternatives`); >0 only when the backend loaded competing
      // candidates as separate cards (e.g. a dropped clarification). The
      // table used to drop them — now they render so the count matches
      // the tracker's "N options" badge and each is addable.
      const optionCount = options.length + 1;
      const v = derivePartView(partResult, index);
      const isAddingSingle = addingParts.has(v.uniqueKey);
      const oe = v.oeNumber || v.partNumber || '—';
      const oeName = v.description || '';
      const translated = oeName ? translationsMap[oeName] : '';
      const nameTrans = translated && translated !== oeName ? translated : '';
      // The resolved label carries the user's original term as a
      // parenthetical that may sit mid-string when qualifiers are
      // appended after it (e.g. "Friso de Porta, Traseiro, Esquerdo
      // (friso porta trás esquerdo), Rear, Left"). Split it with the
      // shared helper: Requested = ONLY the user's verbatim request (the
      // parenthetical); OE Description = the resolved name without it.
      const fullLabel = groupLabel || v.resolvedLabel || '';
      const { partName: labelName } = splitPartAndRef(fullLabel);
      // Requested = the SAME label the tracker list shows — the corrected
      // search name with the user's original term in parentheses, e.g.
      // "Parabrisas (windshield)". Use the paired validation part's full
      // display label; fall back to the result's own resolved label.
      // A part-number request shows the resolved catalog name (translated
      // when available) with the number as the parenthetical reference.
      const requestedTerm = composeNumberRequestLabel(
        groupLine?.original || fullLabel || v.originalQuery || labelName || `#${index + 1}`,
        nameTrans || oeName,
      );
      // Requested LEADS with our reading of the request ("Bumper,
      // Rear") and keeps the customer's own words as the muted
      // parenthetical ("(bumper)") — the same shape as the composed
      // label and the tracker row. With a ledger the line holds both
      // halves separately; otherwise the label is split.
      const reqSplit = splitPartAndRef(requestedTerm);
      const reqFlip = groupLine
        ? {
            primary: groupLine.label || groupLine.original,
            secondary: groupLine.original && groupLine.original !== groupLine.label ? groupLine.original : '',
          }
        : { primary: reqSplit.partName || requestedTerm, secondary: reqSplit.ref || '' };
      return (
        <React.Fragment key={v.uniqueKey}>
        {/* id = the SAME anchor the requested-parts tracker jumps to
            (see PartsTrackerPanel.jumpTo / ChatPage anchorId), so the
            jump + highlight also works when this view is in table mode.
            Cards and table never render together, so no duplicate id. */}
        <tr id={v.uniqueKey}>
          {/* Diagram thumbnail */}
          {renderImageCell(v, partResult, index)}
          {/* Row number — the requested part's stable rank; matches the tracker */}
          <td style={{ ...td, textAlign: 'center', padding: '8px 2px', fontSize: '11px', fontWeight: 700, color: '#9a958c' }}>
            {num ?? ''}
          </td>
          {/* Original requested term (what the user asked for) — the
              user's verbatim line leads; the resolved name is the
              muted secondary so it can't be mistaken for the request. */}
          <td style={{ ...td, ...wrap, fontSize: '11px', color: '#6b6b6b' }}>
            <span style={wrap}>
              <span style={{ fontWeight: 600, color: '#4b4a46' }}>{reqFlip.primary}</span>
              {reqFlip.secondary && (
                <span style={{ color: '#9a958c' }}> ({reqFlip.secondary})</span>
              )}
            </span>
            {canEditResult && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  // Seed the editor with the user's verbatim search,
                  // never the resolved/translated display label.
                  editResult(reqFlip.secondary || v.originalQuery || reqFlip.primary, reqFlip.primary);
                }}
                title={t('chat.partEdit.resultAction', { defaultValue: 'Edit this search' })}
                aria-label={t('chat.partEdit.resultAction', { defaultValue: 'Edit this search' })}
                style={{ marginLeft: '5px', display: 'inline-flex', verticalAlign: 'middle', alignItems: 'center', justifyContent: 'center', width: '22px', height: '22px', color: '#6866ff', backgroundColor: '#f4f2ff', border: '1px solid #ddd9ff', borderRadius: '6px', cursor: 'pointer' }}
              >
                <Pencil size={11} />
              </button>
            )}
            {/* Link to the user's own (annotated) photo this part was
                identified in — vision turns only. */}
            {(() => {
              const vurl = visionImageFor(reqSplit.ref, requestedTerm, v.originalQuery);
              return vurl ? (
                <a
                  href={vurl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={e => e.stopPropagation()}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 3, marginTop: 3, fontSize: '10px', fontWeight: 600, color: '#6866ff', textDecoration: 'none' }}
                  title={t('chat.multiPart.yourPhotoTitle', { defaultValue: 'Open the photo this part was identified in' })}
                >
                  <ZoomIn size={11} /> {t('chat.multiPart.yourPhoto', { defaultValue: 'Your photo' })}
                </a>
              ) : null;
            })()}
          </td>
          {/* OE number */}
          <td style={{ ...td, ...wrap, fontSize: '11px', fontWeight: 700, color: '#201e1a', fontFamily: 'ui-monospace, monospace' }}>
            {oe}
            <SupersessionIndicator row={v.supersessionRow} />
          </td>
          {/* Catalog quantity */}
          <td style={{ ...td, textAlign: 'center', fontSize: '11px', fontWeight: 600, color: '#201e1a' }}>{v.catalogQty}</td>
          {/* OE description (source language) with the translation stacked
              beneath it — one column instead of two, to save width. */}
          <td style={{ ...td, ...wrap, fontSize: '12px', fontWeight: 600, color: '#201e1a' }}>
            {renderOeName(oeName || '', nameTrans || '')}
            {renderPositionChips(partResult)}
            {/* "N options" badge — mirrors the tracker; the rows below are
                the other candidates the search grouped under this request. */}
            {optionCount > 1 && (
              <div style={{ marginTop: '4px' }}>
                <span
                  title={t('chat.multiPart.optionsHint', { defaultValue: '{{n}} matching parts — expand to choose', n: optionCount })}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '10px', fontWeight: 700, color: '#b45309', backgroundColor: '#fef3c7', border: '1px solid #fde68a', borderRadius: '20px', padding: '1px 8px', whiteSpace: 'nowrap' }}
                >
                  {t('chat.multiPart.options', { defaultValue: '{{n}} options', n: optionCount })}
                </span>
              </div>
            )}
          </td>
          {/* OE Notes — remark / notes / optional package (hidden unless a part has notes) */}
          {showNotes && <td style={{ ...td, ...wrap, fontSize: '11px', color: '#6b6b6b' }}>{renderOeNotes(partResult)}</td>}
          {showFrom && <td style={{ ...td, ...wrap, fontSize: '11px', color: '#6b6b6b' }}>{partFromValue(partResult) || '—'}</td>}
            <td style={{ ...td, textAlign: 'center', overflow: 'hidden' }}><SourceBadge kind="epc" /></td>
          {/* A manual choice supersedes the resolver's probability:
              show the selection provenance, not a stale model score. */}
          <td style={{ ...td, minWidth: 0, overflow: 'hidden', textAlign: 'center' }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'center', gap: '4px', minWidth: 0, maxWidth: '100%' }}>
              {!partResult.user_selected && (
                v.probability != null ? (
                  <ProbabilityBadge probability={v.probability} t={t} compact />
                ) : (
                  <span style={{ fontSize: '11px', color: '#c4bfb6' }}>—</span>
                )
              )}
              <ExpertVerdictChip line={groupLine} t={t} />
            </div>
          </td>
          {/* Status — in cart / warning / add to cart */}
          <td style={{ ...td, textAlign: 'center' }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'center', gap: '5px' }}>
              {onAddToCart && (
                v.isInCart ? (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '11px', fontWeight: 600, color: '#137f67' }}>
                    <Check size={13} /> {t('chat.multiPart.inCart', { defaultValue: 'In cart' })}
                  </span>
                ) : (
                  <button
                    onClick={(e) => { e.stopPropagation(); handleAddRowToCart(partResult, index); }}
                    disabled={isAddingSingle}
                    className="hover:opacity-90 transition-opacity"
                    title={t('chat.multiPart.addToCart', { defaultValue: 'Add to Cart' })}
                    style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '3px', fontSize: '10px', fontWeight: 600, color: '#6866ff', backgroundColor: 'transparent', border: '1px solid #6866ff', borderRadius: '6px', padding: '3px 6px', whiteSpace: 'nowrap', maxWidth: '100%', cursor: isAddingSingle ? 'not-allowed' : 'pointer', opacity: isAddingSingle ? 0.6 : 1 }}
                  >
                    {isAddingSingle
                      ? <Loader2 size={13} className="animate-spin" />
                      : <ShoppingCart size={13} />}
                    {t('chat.multiPart.add', { defaultValue: 'Add' })}
                  </button>
                )
              )}
            </div>
          </td>
        </tr>
        {options.map(([altPr, altIdx]) => renderAltOptionRow(altPr, altIdx))}
        </React.Fragment>
      );
    };
    // When the "full list" is on, show the earlier-iteration (history) rows FIRST
    // — it's the past — then this iteration's new rows. Each block is already
    // sorted by # (historyRows and tableRows are pre-sorted), so it reads in
    // order within each section without burying the new result at the bottom.
    // Full list = everything, ordered simply by # (ascending). Each row shows its
    // own status badge; no history-vs-current grouping.
    // Under the compact unresolved list the table shows ONLY the rest of the
    // order: this turn's line is already on screen above it.
    const rowsToRender: Array<any> = missOnly
      ? historyRows
      : showHistory && historyRows.length > 0
      ? [...historyRows, ...tableRows].sort((a, b) => {
          if (a.num != null && b.num != null) return a.num - b.num || a.ord - b.ord;
          if (a.num != null) return -1;
          if (b.num != null) return 1;
          return a.ord - b.ord;
        })
      : tableRows;
    const allRowParts = [...(parts_results || []), ...(priorParts || [])];
    // Optional "From" column — only shown when at least one part carries a `from`
    // (availability/effective date) in its extra fields.
    const showFrom = allRowParts.some(pr => partFromValue(pr) !== '');
    // OE Notes column is HIDDEN by default — notes are rarely needed to review a
    // match; only surface the column when at least one part actually has a note
    // (the "dilemma" case). Keeps the table lean and readable.
    const showNotes = allRowParts.some(pr => partNoteValues(pr).length > 0);
    // Always-present columns: Image, #, Requested, OE#, Cant., Description,
    // Source, Prob, Actions (9) + optional OE Notes + optional From.
    // Description takes the remainder so the widths always sum to 100%.
    const colSpanAll = 9 + (showNotes ? 1 : 0) + (showFrom ? 1 : 0);
    const wSource = 13;
    const wImage = showFrom ? 5 : 6;
    const wReq = showFrom ? 13 : 14;
    const wOe = showFrom ? 11 : 12;
    const wCant = 5;
    const wNotes = showFrom ? 15 : 18;
    // Named so the budget below and the <col> widths can't drift apart — they
    // were duplicated magic numbers. Prob. carries the match band plus two 22px
    // icons (manual selection, expert verdict), which wrap under it when tight.
    const wProb = 8;
    const wActions = 14;
    const fixedW = wImage + 4 + wReq + wOe + wCant + (showNotes ? wNotes : 0) + (showFrom ? 11 : 0) + wSource + wProb + wActions;
    const wDesc = Math.max(18, 100 - fixedW);
    return (
      <div
        className="notranslate"
        // translate="no": browser auto-translate (Google Translate) rewrites text
        // nodes inside these rows with <font> wrappers; React then crashes on
        // reconciliation with "insertBefore … not a child of this node". Row content
        // is already localized server-side via client_language.
        translate="no"
        style={{ border: '1px solid #ebe7df', borderRadius: '10px', overflow: 'hidden', backgroundColor: '#ffffff' }}
      >
        <div style={{ overflowX: 'auto' }}>
          {/* No minWidth → the table always fits its container, so there's no
              horizontal scrollbar; text wraps instead. Percentage widths keep the
              columns proportional at any width. */}
          <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
            <colgroup>
              <col style={{ width: `${wImage}%` }} />{/* Image */}
              <col style={{ width: '4%' }} />{/* # (tracking number) */}
              <col style={{ width: `${wReq}%` }} />{/* Requested term */}
              <col style={{ width: `${wOe}%` }} />{/* OE# */}
              <col style={{ width: `${wCant}%` }} />{/* Cant. (catalog qty) */}
              <col style={{ width: `${wDesc}%` }} />{/* OE description */}
              {showNotes && <col style={{ width: `${wNotes}%` }} />}{/* OE Notes */}
              {showFrom && <col style={{ width: '11%' }} />}{/* From */}
              <col style={{ width: `${wSource}%` }} />{/* Source */}
              <col style={{ width: `${wProb}%` }} />{/* Probability + "Manual Selection" chip */}
              <col style={{ width: `${wActions}%` }} />{/* Actions — wide enough for the localized "Agregar" button + header */}
            </colgroup>
            <thead>
              <tr style={{ backgroundColor: '#f6f4f0' }}>
                <th style={th} aria-hidden="true" />{/* Image */}
                <th style={{ ...th, textAlign: 'center', padding: '8px 2px' }}>#</th>
                <th style={th}>{t('chat.multiPart.requestedTerm', { defaultValue: 'Requested' })}</th>
                <th style={th}>{t('chat.multiPart.oeNumber', { defaultValue: 'OE#' })}</th>
                <th style={{ ...th, textAlign: 'center' }}>{t('chat.multiPart.qty', { defaultValue: 'Qty' })}</th>
                <th style={th}>{t('chat.multiPart.oeDescription', { defaultValue: 'OE Description' })}</th>
                {showNotes && <th style={th}>{t('chat.multiPart.oeNotes', { defaultValue: 'OE Notes' })}</th>}
                {showFrom && <th style={th}>{t('chat.multiPart.from', { defaultValue: 'From' })}</th>}
                <th style={{ ...th, textAlign: 'center' }}>{t('chat.multiPart.source', { defaultValue: 'Source' })}</th>
                <th style={{ ...th, textAlign: 'center' }}>{t('chat.multiPart.probability', { defaultValue: 'Prob.' })}</th>
                <th style={{ ...th, textAlign: 'center' }}>{t('chat.multiPart.actions', { defaultValue: 'Actions' })}</th>
              </tr>
            </thead>
            <tbody className="stagger">
              {/* Toggle the collapsed grayed "full list" of earlier-iteration parts —
                  at the top so the history reveals right below it, before the new rows. */}
              {historyRows.length > 0 && !missOnly && (
                <tr>
                  <td colSpan={colSpanAll} style={{ padding: '8px 12px', borderBottom: '1px solid #f0ede9', backgroundColor: '#faf9f7' }}>
                    <button
                      type="button"
                      onClick={() => setShowHistory(v => !v)}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '11px', fontWeight: 600, color: '#6866ff', background: 'transparent', border: 'none', cursor: 'pointer', padding: 0 }}
                    >
                      {showHistory ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                      {ledger
                        ? (showHistory
                            ? t('chat.multiPart.hideOrder', { defaultValue: 'Hide the rest of the order' })
                            : t('chat.multiPart.showOrder', { defaultValue: 'Show the full order (+{{n}} more)', n: historyRows.length }))
                        : (showHistory
                            ? t('chat.multiPart.hideHistory', { defaultValue: 'Hide earlier parts' })
                            : t('chat.multiPart.showHistory', { defaultValue: 'Show full list (+{{n}} earlier)', n: historyRows.length }))}
                    </button>
                  </td>
                </tr>
              )}
              {/* ONE row per requested part (partGroup), so the table count lines up
                  with foundCount + the tracker. A group's hero is its first candidate;
                  any other candidates for the SAME request are grouped into the
                  expanded row (see "Other matching parts" below) rather than shown as
                  separate look-alike rows. */}
              {rowsToRender.map((row, ri) => {
                // ── History (found in an earlier iteration) — full clickable row,
                //    just hidden by default behind the "full list" toggle ──
                if (row.kind === 'history') {
                  const pr = row.part as PartResult | undefined;
                  const hLine: RequestLine | undefined = (row as any).line;
                  // A line from an earlier turn whose card is not in this
                  // snapshot (a session that predates card→line binding). Show
                  // the line and its status rather than dropping it from the
                  // order.
                  if (!pr) {
                    const chip = notFoundChip(hLine?.original || '', hLine);
                    return (
                      <tr key={`hist-${ri}`}>
                        <td style={td} aria-hidden="true" />
                        <td style={{ ...td, textAlign: 'center', padding: '8px 2px', fontSize: '11px', fontWeight: 700, color: '#9a958c' }}>{row.num ?? ''}</td>
                        <td style={{ ...td, ...wrap, fontSize: '11px', color: '#6b6b6b' }}>
                          <span style={wrap}>{hLine?.original || ''}</span>
                          <div title={chip.title} style={{ display: 'flex', alignItems: 'center', gap: '3px', marginTop: '2px', fontSize: '10px', fontWeight: 600, color: chip.color, ...wrap }}>
                            {chip.label}
                          </div>
                        </td>
                        <td style={{ ...td, color: '#c4bfb6', fontSize: '11px' }}>—</td>
                        <td style={{ ...td, textAlign: 'center', color: '#c4bfb6', fontSize: '11px' }}>—</td>
                        <td style={{ ...td, color: '#c4bfb6', fontSize: '12px' }}>—</td>
                        {showNotes && <td style={{ ...td, color: '#c4bfb6', fontSize: '11px' }}>—</td>}
                        {showFrom && <td style={{ ...td, color: '#c4bfb6', fontSize: '11px' }}>—</td>}
                        <td style={{ ...td, textAlign: 'center', color: '#c4bfb6', fontSize: '11px' }}>—</td>
                        <td style={{ ...td, textAlign: 'center', color: '#c4bfb6', fontSize: '11px' }}>—</td>
                        <td style={td} />
                      </tr>
                    );
                  }
                  // Same row as the search that found it: its line's label,
                  // the pencil, the verdict, every option it loaded.
                  const hIndex = 900000 + ri * 100;
                  return renderFoundRow(
                    pr, hIndex, row.num, hLine, hLine?.label || '',
                    ((row as any).parts as PartResult[] || []).slice(1).map((alt, k) => [alt, hIndex + k + 1] as [PartResult, number]),
                  );
                }
                if (row.kind === 'not_found') {
                  const part = row.term;
                  const rowLine: RequestLine | undefined = (row as any).line;
                  // A line still being searched, or waiting on the customer, is
                  // not a miss: offering "research it" there would ask them to
                  // redo work that is already under way or already theirs to do.
                  const settledMiss = !rowLine
                    || (rowLine.status !== 'searching' && rowLine.status !== 'needs_answer' && rowLine.status !== 'found');
                  const trans = translationsMap[part];
                  const suggestions = settledMiss ? suggestionsFor(part) : [];
                  // Data-source hits replace the not-found row entirely — a red
                  // "Not found" next to matching suggestions reads as a
                  // contradiction (product feedback). The strip header carries
                  // the requested term instead.
                  if (suggestions.length > 0) {
                    return (
                      <React.Fragment key={`nf-${ri}`}>
                        {renderSuggestionsRow(suggestions, part, rowLine)}
                      </React.Fragment>
                    );
                  }
                  // A skipped question: the row says so and offers the two ways
                  // to finish it — the diagram, or more context in the AI chat.
                  const skippedQid = rowLine && rowLine.status === 'needs_answer' && rowLine.reason === 'SKIPPED'
                    && rowLine.question_id && onPostponedAction && (postponedQuestionIds || []).includes(rowLine.question_id)
                    ? rowLine.question_id : null;
                  return (
                    <React.Fragment key={`nf-${ri}`}>
                    <tr style={skippedQid ? { backgroundColor: '#fffaeb' } : undefined} data-testid={skippedQid ? 'skipped-row' : undefined}>
                      <td style={td} aria-hidden="true" />{/* Image */}
                      <td style={{ ...td, textAlign: 'center', padding: '8px 2px', fontSize: '11px', fontWeight: 700, color: '#9a958c' }}>{row.num ?? ''}</td>
                      <td style={{ ...td, ...wrap, fontSize: '11px', color: '#6b6b6b' }}>
                        <span style={wrap}>{part}</span>
                        {trans && trans !== part && (
                          <div style={{ fontSize: '10px', color: '#9a958c' }}>{trans}</div>
                        )}
                        {/* Status text lives here (it can wrap) — the narrow
                            PROB. column only gets an icon badge; the old nowrap
                            pill overflowed it and overlapped the Actions cell. */}
                        {(() => { const chip = notFoundChip(part, rowLine); return (
                        <div title={chip.title} style={{ display: 'flex', alignItems: 'center', gap: '3px', marginTop: '2px', fontSize: '10px', fontWeight: 600, color: chip.color, ...wrap }}>
                          {chip.amber ? <AlertTriangle size={11} style={{ flexShrink: 0 }} /> : <SearchX size={11} style={{ flexShrink: 0 }} />}
                          {chip.label}
                        </div>
                        ); })()}
                      </td>
                      {skippedQid ? (
                        <td style={td} colSpan={4 + (showNotes ? 1 : 0) + (showFrom ? 1 : 0)}>
                          <div style={{ fontSize: '12.5px', color: '#3d3b45', marginBottom: 6 }}>
                            {t('chat.multiPart.skippedRowLead', { defaultValue: 'Select the part from the illustration, or add context:' })}
                          </div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); onPostponedAction!(skippedQid, 'diagram'); }}
                              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 14px', borderRadius: 999, border: '1.5px solid #6866ff', background: '#ffffff', color: '#4b48e8', fontSize: '12.5px', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}
                            >
                              <Maximize2 size={13} />
                              {t('chat.clarification.openEpcDiagram', { defaultValue: 'Open the EPC diagram' })}
                            </button>
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); onPostponedAction!(skippedQid, 'chat'); }}
                              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 14px', borderRadius: 999, border: 'none', background: '#6866ff', color: '#ffffff', fontSize: '12.5px', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}
                            >
                              <Sparkles size={13} />
                              {t('chat.multiPart.addContextAiChat', { defaultValue: 'Add context · AI Chat' })}
                            </button>
                          </div>
                        </td>
                      ) : (<>
                      <td style={{ ...td, textAlign: 'center', color: '#c4bfb6', fontSize: '11px' }}>—</td>
                      <td style={{ ...td, textAlign: 'center', color: '#c4bfb6', fontSize: '11px' }}>—</td>{/* Cant. */}
                      <td style={{ ...td, color: '#c4bfb6', fontSize: '12px' }}>—</td>
                      {showNotes && <td style={{ ...td, color: '#c4bfb6', fontSize: '11px' }}>—</td>}{/* OE Notes */}
                      {showFrom && <td style={{ ...td, color: '#c4bfb6', fontSize: '11px' }}>—</td>}{/* From */}
                      <td style={{ ...td, textAlign: 'center', color: '#c4bfb6', fontSize: '11px' }}>—</td>
                      </>)}
                      <td style={{ ...td, textAlign: 'center' }}>
                        {(() => { const chip = notFoundChip(part, rowLine); return (
                        <span
                          title={chip.title}
                          style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '22px', height: '22px', color: chip.color, backgroundColor: chip.bg, border: `1px solid ${chip.border}`, borderRadius: '50%' }}
                        >
                          {chip.amber ? <AlertTriangle size={12} /> : <SearchX size={12} />}
                        </span>
                        ); })()}
                        <ExpertVerdictChip line={rowLine} t={t} />
                      </td>
                      <td style={td}>
                        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '5px' }}>
                          {settledMiss && canEditResult && (
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); editResult(part); }}
                              className="hover:opacity-90 transition-opacity"
                              title={t('chat.partEdit.resultAction', { defaultValue: 'Edit this search' })}
                              style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', fontSize: '10px', fontWeight: 600, color: '#6866ff', backgroundColor: 'transparent', border: '1px solid #6866ff', borderRadius: '6px', padding: '3px 6px', whiteSpace: 'nowrap', cursor: 'pointer' }}
                            >
                              <Pencil size={13} />
                              {t('chat.multiPart.refine', { defaultValue: 'Refine' })}
                            </button>
                          )}
                          {settledMiss && onAddToCart && (() => {
                            // Same "add anyway" the not-found banner offers in cards
                            // view — the part goes into the cart as a not-found line.
                            const nfInCart = cartPartIdentifiers.has(part.toLowerCase());
                            const nfKey = `not-found-${part}`;
                            const nfAdding = addingParts.has(nfKey);
                            return nfInCart ? (
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '11px', fontWeight: 600, color: '#137f67' }}>
                                <Check size={13} /> {t('chat.multiPart.inCart', { defaultValue: 'In cart' })}
                              </span>
                            ) : (
                              <button
                                type="button"
                                disabled={nfAdding}
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  flushSync(() => { setAddingParts(prev => new Set(prev).add(nfKey)); });
                                  try {
                                    await onAddToCart({ partNumber: part, description: part, not_found: true, translated_description: translationsMap[part] || null }, structuredData);
                                  } finally {
                                    setAddingParts(prev => { const next = new Set(prev); next.delete(nfKey); return next; });
                                  }
                                }}
                                className="hover:opacity-90 transition-opacity"
                                title={t('chat.multiPart.addToCart', { defaultValue: 'Add to cart' })}
                                style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '3px', fontSize: '10px', fontWeight: 600, color: '#6866ff', backgroundColor: 'transparent', border: '1px solid #6866ff', borderRadius: '6px', padding: '3px 6px', whiteSpace: 'nowrap', cursor: nfAdding ? 'not-allowed' : 'pointer', opacity: nfAdding ? 0.6 : 1 }}
                              >
                                {nfAdding ? <Loader2 size={13} className="animate-spin" /> : <ShoppingCart size={13} />}
                                {t('chat.multiPart.add', { defaultValue: 'Add' })}
                              </button>
                            );
                          })()}
                        </div>
                      </td>
                    </tr>
                    {suggestions.length > 0 && renderSuggestionsRow(suggestions, part)}
                    </React.Fragment>
                  );
                }
                // ── Found result ──
                const group = row.group as FoundGroup;
                return renderFoundRow(
                  parts_results[group.indices[0]], group.indices[0], group.num,
                  (group as any).line, group.label,
                  group.indices.slice(1).map((oi: number) => [parts_results[oi], oi] as [PartResult, number]),
                );
              })}
              {/* Unresolved (no_result) requests and orphan suggestion keys
                  have no row of their own — surface their data-source hits as
                  trailing strips. */}
              {(() => {
                // Every requested line owns a row that states its own outcome, so
                // nothing needs a second, trailing strip to say the same thing.
                const nioRendered = new Set<string>();
                return [...leftoverSuggestionTerms]
                  .filter(t => !nioRendered.has(t.trim().toLowerCase()))
                  .map((term, ui) => {
                    const suggestions = suggestionsFor(term);
                    if (suggestions.length === 0) return null;
                    return (
                      <React.Fragment key={`unres-${ui}`}>
                        {renderSuggestionsRow(suggestions, term)}
                      </React.Fragment>
                    );
                  });
              })()}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // Export the table data as a CSV in the STANDARD export format shared by every
  // parts/cart/order CSV — OE#, Requested, Description, Qty, Unit (CUR),
  // Total (CUR). Search results usually aren't priced yet (pricing happens in
  // the cart via the DMS DAG), so Unit/Total are filled only when the result
  // carries a numeric price (e.g. data-source suggestions) and left blank
  // otherwise. A UTF-8 BOM is prefixed so Excel renders accented (e.g. Spanish)
  // text correctly.
  const handleDownloadCsv = () => {
    const escapeCsv = (val: unknown) => {
      const s = (val ?? '').toString();
      return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const cur = currentUser?.currency || '';
    const curSuffix = cur ? ` (${cur})` : '';
    // Header labels reuse the cart table's keys so every CSV export shares the
    // exact same (translated) headers.
    const headers = [
      t('cart.sidePanel.table.oeNumber', { defaultValue: 'OE#' }),
      t('cart.sidePanel.table.requested', { defaultValue: 'Requested' }),
      t('cart.sidePanel.table.description', { defaultValue: 'Description' }),
      t('cart.sidePanel.table.qty', { defaultValue: 'Qty' }),
      `${t('cart.sidePanel.table.unitPrice', { defaultValue: 'Unit' })}${curSuffix}`,
      `${t('cart.sidePanel.table.total', { defaultValue: 'Total' })}${curSuffix}`,
    ];
    // Export the FULL list = earlier-iteration (history) parts first, then this
    // iteration's rows — the same merged list the "Show full list" toggle reveals.
    const fullList: any[] = [...historyRows, ...tableRows].sort((a, b) => {
      if (a.num != null && b.num != null) return a.num - b.num || a.ord - b.ord;
      if (a.num != null) return -1;
      if (b.num != null) return 1;
      return a.ord - b.ord;
    });
    const rows = fullList.map((r, i) => {
      if (r.kind === 'not_in_oe') {
        const it = r.item as SummaryItem;
        const req = requestedOriginalTerm(it.displayLabel || it.name);
        return ['', req, t('chat.botMessage.notInOe', { defaultValue: 'Not in catalog' }), '', '', ''];
      }
      if (r.kind === 'not_found') {
        // The line states its own outcome, so the export says what the table
        // said rather than flattening every unsettled line to "Not found".
        const chip = r.line ? lineChip(r.line) : null;
        const status = chip
          ? t(chip.key, { defaultValue: chip.fallback })
          : t('chat.multiPart.notFoundBadge', { defaultValue: 'Not found' });
        return ['', requestedOriginalTerm(r.term), status, '', '', ''];
      }
      // 'found' (this iteration) or 'history' (earlier) — both carry a PartResult.
      const pr: PartResult | undefined = r.kind === 'history' ? r.part : parts_results[r.group.indices[0]];
      if (!pr) {
        // A line found in an earlier search whose card is not in this snapshot.
        const chip = r.line ? lineChip(r.line) : null;
        return ['', requestedOriginalTerm(r.term || r.line?.original || ''),
          chip ? t(chip.key, { defaultValue: chip.fallback }) : '', '', '', ''];
      }
      const v = derivePartView(pr, 700000 + i);
      const oe = v.oeNumber || v.partNumber || '';
      const oeName = v.description || '';
      const oeTrans = oeName ? (translationsMap[oeName] || '') : '';
      const nameTrans = oeTrans && oeTrans !== oeName ? oeTrans : '';
      // Requested = ONLY the user's own line. The table shows the composed
      // "Resolved Name (original)" label, but the export must carry what the user
      // typed — it used to emit the whole label ("Bumper Spoiler, Rear
      // (Para-choques trás (spoiler))"). requestedOriginalTerm pulls the
      // parenthetical out; the cart and order CSVs do the same, via the same helper.
      let requestedTerm: string;
      // The line holds the customer's own wording; the card is the fallback for
      // a row that somehow has no line.
      requestedTerm = requestedOriginalTerm(
        (r.line?.original
          || pr.related_user_query
          || pr.related_corrected_name
          || v.resolvedLabel
          || v.originalQuery
          || '').toString(),
      );
      // Number-request rows lead with the resolved catalog name (same as the table).
      requestedTerm = composeNumberRequestLabel(requestedTerm, nameTrans || oeName);
      // Description = translated catalog name when we have one, else the OE name.
      const description = nameTrans || oeName;
      const qty = v.catalogQty ?? 1;
      // Unit price only when the result already carries a numeric price (rare
      // pre-cart; e.g. data-source rows). pricing_data strings are NOT parsed.
      const unitRaw = (v.heroPart as any)?.price ?? (v.pd as any)?.price;
      const unit = unitRaw != null && unitRaw !== '' && isFinite(Number(unitRaw)) ? Number(unitRaw) : null;
      const total = unit != null ? Math.round(unit * qty * 100) / 100 : null;
      return [oe, requestedTerm, description, qty, unit ?? '', total ?? ''];
    });
    const csv = [headers, ...rows].map(r => r.map(escapeCsv).join(',')).join('\r\n');
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `parts-${messageId}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // A turn that confirmed nothing has no columns worth a table: nine headers
  // over a row of dashes, with the actions squeezed into the last one. It gets
  // the same card the rest of the chat uses, one line per request, with the
  // outcome spelled out and the two actions given room.
  const missOnly = parts_results.length === 0
    && ledger.rows.length > 0
    && ledger.rows.every(r => r.kind === 'not_found')
    && !ledger.rows.some(r => r.kind === 'not_found' && suggestionsFor(r.term).length > 0);

  const renderUnresolved = () => (
    <div
      className="notranslate"
      translate="no"
      style={{ border: '1px solid #ebe7df', borderRadius: '10px', overflow: 'hidden', backgroundColor: '#ffffff' }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', padding: '7px 12px', backgroundColor: '#faf9f7', borderBottom: '1px solid #f0ede9' }}>
        <span style={{ fontSize: '10px', fontWeight: 700, letterSpacing: '0.4px', textTransform: 'uppercase', color: '#7c6f5a' }}>
          {t('chat.multiPart.unresolvedTitle', { defaultValue: 'Unresolved' })}
          <span style={{ marginLeft: '6px', fontSize: '10px', fontWeight: 700, color: '#92400e', backgroundColor: '#FEF3C7', border: '1px solid #f59e0b', borderRadius: '20px', padding: '0 6px' }}>
            {ledger.rows.length}
          </span>
        </span>
      </div>
      {ledger.rows.map((row, ri) => {
        if (row.kind !== 'not_found') return null;
        const part = row.term;
        const chip = notFoundChip(part, row.line);
        const trans = translationsMap[part];
        const settled = row.line.status !== 'searching' && row.line.status !== 'needs_answer' && row.line.status !== 'found';
        const nfKey = `not-found-${part}`;
        const nfAdding = addingParts.has(nfKey);
        const nfInCart = cartPartIdentifiers.has(part.toLowerCase());
        return (
          <div key={`unres-${ri}`} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '10px 12px', borderBottom: ri < ledger.rows.length - 1 ? '1px solid #f0ede9' : 'none' }}>
            <span style={{ fontSize: '11px', fontWeight: 700, color: '#9a958c', minWidth: '16px', paddingTop: '2px' }}>{row.num || ''}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: '13px', fontWeight: 600, color: '#201e1a', overflowWrap: 'anywhere' }}>{part}</div>
              {trans && trans !== part && (
                <div style={{ fontSize: '11px', color: '#9a958c' }}>{trans}</div>
              )}
              <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '6px', marginTop: '4px' }}>
                <span
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '10px', fontWeight: 700, color: chip.color, backgroundColor: chip.bg, border: `1px solid ${chip.border}`, borderRadius: '20px', padding: '1px 8px', whiteSpace: 'nowrap' }}
                >
                  {chip.amber ? <AlertTriangle size={11} /> : <SearchX size={11} />}
                  {chip.label}
                </span>
                <span style={{ fontSize: '11px', color: '#6b6b6b', overflowWrap: 'anywhere' }}>{chip.title}</span>
              </div>
            </div>
            {settled && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
                {canEditResult && (
                  // The customer re-words the line in the same refine popup a
                  // found row's pencil opens — a search that missed is most
                  // often a wording problem, not one to run again unchanged.
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); editResult(part); }}
                    className="hover:opacity-90 transition-opacity"
                    title={t('chat.partEdit.resultAction', { defaultValue: 'Edit this search' })}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '11px', fontWeight: 600, color: '#6866ff', backgroundColor: 'transparent', border: '1px solid #6866ff', borderRadius: '6px', padding: '4px 9px', whiteSpace: 'nowrap', cursor: 'pointer' }}
                  >
                    <Pencil size={13} />
                    {t('chat.multiPart.refine', { defaultValue: 'Refine' })}
                  </button>
                )}
                {onAddToCart && (nfInCart ? (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '11px', fontWeight: 600, color: '#137f67' }}>
                    <Check size={13} /> {t('chat.multiPart.inCart', { defaultValue: 'In cart' })}
                  </span>
                ) : (
                  <button
                    type="button"
                    disabled={nfAdding}
                    onClick={async (e) => {
                      e.stopPropagation();
                      flushSync(() => { setAddingParts(prev => new Set(prev).add(nfKey)); });
                      try {
                        await onAddToCart({ partNumber: part, description: part, not_found: true, translated_description: translationsMap[part] || null }, structuredData);
                      } finally {
                        setAddingParts(prev => { const next = new Set(prev); next.delete(nfKey); return next; });
                      }
                    }}
                    className="hover:opacity-90 transition-opacity"
                    title={t('chat.multiPart.addToCart', { defaultValue: 'Add to cart' })}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '11px', fontWeight: 600, color: '#6866ff', backgroundColor: 'transparent', border: '1px solid #6866ff', borderRadius: '6px', padding: '4px 9px', whiteSpace: 'nowrap', cursor: nfAdding ? 'not-allowed' : 'pointer', opacity: nfAdding ? 0.6 : 1 }}
                  >
                    {nfAdding ? <Loader2 size={13} className="animate-spin" /> : <ShoppingCart size={13} />}
                    {t('chat.multiPart.add', { defaultValue: 'Add' })}
                  </button>
                ))}
              </div>
            )}
          </div>
        );
      })}
      {historyRows.length > 0 && (
        <div style={{ padding: '8px 12px', borderTop: '1px solid #f0ede9', backgroundColor: '#faf9f7' }}>
          <button
            type="button"
            onClick={() => setShowHistory(v => !v)}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '11px', fontWeight: 600, color: '#6866ff', background: 'transparent', border: 'none', cursor: 'pointer', padding: 0 }}
          >
            {showHistory ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            {showHistory
              ? t('chat.multiPart.hideOrder', { defaultValue: 'Hide the rest of the order' })
              : t('chat.multiPart.showOrder', { defaultValue: 'Show the full order (+{{n}} more)', n: historyRows.length })}
          </button>
        </div>
      )}
    </div>
  );

  // Segmented Cards/Table control shown above the results.
  const renderViewToggle = () => {
    const segBtn = (active: boolean): React.CSSProperties => ({
      display: 'inline-flex', alignItems: 'center', gap: '5px',
      padding: '5px 10px', fontSize: '11px', fontWeight: 600,
      color: active ? '#6866ff' : '#8a857c',
      backgroundColor: active ? '#efedff' : 'transparent',
      border: 'none', cursor: 'pointer',
    });
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
        <span style={{ fontSize: '12px', fontWeight: 600, color: '#9a958c' }}>
          {ledger
            ? (ledger.orderTotal > ledger.turnTotal
                // Two numbers, because they answer two different questions:
                // what this search did, and where the order stands.
                ? t('chat.multiPart.resultsCountOrder', {
                    defaultValue: 'This search {{found}} of {{total}} · Order {{orderFound}} of {{orderTotal}}',
                    found: ledger.turnFound, total: ledger.turnTotal,
                    orderFound: ledger.orderFound, orderTotal: ledger.orderTotal,
                  })
                : t('chat.multiPart.resultsCountOf', {
                    defaultValue: '{{found}} of {{total}} found',
                    found: ledger.turnFound, total: ledger.turnTotal,
                  }))
            : t('chat.multiPart.resultsCount', { defaultValue: '{{count}} found', count: foundCount })}
        </span>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
        {/* Re-search several not-found parts at once (checked via each row's box). */}
        {onSearchPart && researchSel.size > 0 && (
          <button
            type="button"
            onClick={() => runResearch(Array.from(researchSel))}
            title={t('chat.multiPart.researchSelectedTitle', { defaultValue: 'Search the selected parts together' })}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '5px 10px', fontSize: '11px', fontWeight: 600, color: '#ffffff', backgroundColor: '#6866ff', border: '1px solid #6866ff', borderRadius: '8px', cursor: 'pointer' }}
          >
            <Search size={13} /> {t('chat.multiPart.researchSelected', { defaultValue: 'Research selected ({{n}})', n: researchSel.size })}
          </button>
        )}
        {/* Download CSV — available in both views (exports the same table columns
            regardless of which layout is currently shown). */}
        <button
          type="button"
          onClick={handleDownloadCsv}
          title={t('chat.multiPart.downloadCsv', { defaultValue: 'Download CSV' })}
          style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '5px 10px', fontSize: '11px', fontWeight: 600, color: '#6866ff', backgroundColor: 'transparent', border: '1px solid #c5c4ff', borderRadius: '8px', cursor: 'pointer' }}
        >
          <Download size={13} /> {t('chat.multiPart.csv', { defaultValue: 'CSV' })}
        </button>
        <div style={{ display: 'inline-flex', border: '1px solid #e5e3df', borderRadius: '8px', overflow: 'hidden' }} role="group" aria-label={t('chat.multiPart.viewMode', { defaultValue: 'Results view' })}>
          <button type="button" onClick={() => setViewMode('cards')} style={segBtn(viewMode === 'cards')} aria-pressed={viewMode === 'cards'}>
            <LayoutGrid size={13} /> {t('chat.multiPart.cardsView', { defaultValue: 'Cards' })}
          </button>
          <button type="button" onClick={() => setViewMode('table')} style={segBtn(viewMode === 'table')} aria-pressed={viewMode === 'table'}>
            <Table2 size={13} /> {t('chat.multiPart.tableView', { defaultValue: 'Table' })}
          </button>
        </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-3 card-enter">

      {resultEditItems && (
        <RefinePopup
          items={resultEditItems}
          t={t}
          onSubmit={submitResultEdit}
          onClose={() => setResultEditItems(null)}
        />
      )}

      {/* Cards ⇄ Table toggle — only meaningful when there are found parts. */}
      {foundCount > 0 && renderViewToggle()}

      {/* Not found parts — cards view only. The table renders its own not-found
          rows (with the same Research / Add actions), so showing this list next
          to it would duplicate every not-found part. */}
      {missOnly && renderUnresolved()}

      {viewMode === 'cards' && !missOnly && (() => {
        // Parts with data-source suggestions leave the red list — their
        // suggestion strip (term in its header) replaces the not-found row.
        // Unresolved (no_result) requests join with strips of their own.
        const nfList = effectiveNotFound;
        const unresolvedWithSuggestions = leftoverSuggestionTerms.filter(t => suggestionsFor(t).length > 0);
        if (nfList.length === 0 && unresolvedWithSuggestions.length === 0) return null;
        const trulyNotFound = nfList.filter(p => suggestionsFor(p).length === 0);
        return (
        <div style={{ backgroundColor: '#ffffff', border: '1px solid #ebe7df', borderRadius: '12px', overflow: 'hidden' }}>
          {trulyNotFound.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', padding: '7px 12px', backgroundColor: '#faf9f7', borderBottom: '1px solid #f0ede9' }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '10px', fontWeight: 700, letterSpacing: '0.4px', textTransform: 'uppercase', color: '#b91c1c' }}>
              <SearchX size={13} />
              {t('chat.multiPart.notFound', { defaultValue: 'Not found:' }).replace(/[:：]\s*$/, '')}
              <span style={{ fontSize: '10px', fontWeight: 700, color: '#b91c1c', backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '20px', padding: '0 6px' }}>
                {trulyNotFound.length}
              </span>
            </span>
            {onSearchPart && trulyNotFound.length > 1 && (
              <button
                onClick={handleSearchSelectedNotFound}
                disabled={selectedNotFound.size === 0}
                className="transition-colors duration-200"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '3px 8px',
                  fontSize: '10px', fontWeight: 600, borderRadius: '6px',
                  ...(selectedNotFound.size === 0
                    ? { backgroundColor: 'transparent', color: '#b3ada3', border: '1px solid #e5e3df', cursor: 'default' }
                    : { backgroundColor: '#6866ff', color: '#ffffff', border: '1px solid #6866ff', cursor: 'pointer' }),
                }}
                title={t('chat.multiPart.searchSelectedTitle', { defaultValue: 'Search all selected parts together' })}
              >
                <Search size={12} />
                {t('chat.multiPart.searchSelected', { defaultValue: 'Search selected ({{count}})', count: selectedNotFound.size })}
              </button>
            )}
          </div>
          )}
          {nfList.map((part, idx) => {
            const suggestions = suggestionsFor(part);
            const trans = translationsMap[part];
            if (suggestions.length > 0) {
              // Suggestion strip replaces the not-found row (term in header).
              return (
                <div key={`not-found-${idx}`} style={{ padding: '8px 12px' }}>
                  {renderCardSuggestions(suggestions, part)}
                </div>
              );
            }
            return (
              <React.Fragment key={`not-found-${idx}`}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap', padding: '8px 12px', borderTop: idx > 0 ? '1px solid #f5f2ee' : 'none' }}>
                  {onSearchPart && trulyNotFound.length > 1 ? (
                    <label className="min-w-0 cursor-pointer" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <input
                        type="checkbox"
                        checked={selectedNotFound.has(part)}
                        onChange={() => toggleNotFound(part)}
                        style={{ accentColor: '#6866ff', width: '14px', height: '14px', flexShrink: 0, cursor: 'pointer' }}
                      />
                      <span className="truncate" style={{ fontSize: '13px', fontWeight: 600, color: '#201e1a' }}>
                        {part}
                        {trans && trans !== part && (
                          <span style={{ fontSize: '11px', fontWeight: 400, color: '#9a958c', marginLeft: '6px' }}>{trans}</span>
                        )}
                      </span>
                    </label>
                  ) : (
                    <span className="truncate" style={{ fontSize: '13px', fontWeight: 600, color: '#201e1a' }}>
                      {part}
                      {trans && trans !== part && (
                        <span style={{ fontSize: '11px', fontWeight: 400, color: '#9a958c', marginLeft: '6px' }}>{trans}</span>
                      )}
                    </span>
                  )}
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', flexShrink: 0 }}>
                    {(() => { const chip = notFoundChip(part); return chip.amber ? (
                      <span title={chip.title} style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', fontSize: '10px', fontWeight: 600, color: chip.color, backgroundColor: chip.bg, border: `1px solid ${chip.border}`, borderRadius: '4px', padding: '1px 6px', whiteSpace: 'nowrap' }}>
                        <AlertTriangle size={11} /> {chip.label}
                      </span>
                    ) : null; })()}
                    {onSearchPart && (
                      <button
                        onClick={() => onSearchPart(part)}
                        className="hover:opacity-90 transition-opacity"
                        style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '11px', fontWeight: 600, color: '#6866ff', backgroundColor: 'transparent', border: '1px solid #6866ff', borderRadius: '6px', padding: '4px 8px', whiteSpace: 'nowrap', cursor: 'pointer' }}
                        title={t('chat.multiPart.researchPart', { defaultValue: 'Search for this part' })}
                      >
                        <Search size={13} />
                        {t('chat.multiPart.research', { defaultValue: 'Research' })}
                      </button>
                    )}
                    {onAddToCart && (() => {
                      const isInCart = cartPartIdentifiers.has(part.toLowerCase());
                      const notFoundKey = `not-found-${part}`;
                      const isAddingNotFound = addingParts.has(notFoundKey);
                      return isInCart ? (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '11px', fontWeight: 600, color: '#137f67' }}>
                          <Check size={13} /> {t('chat.multiPart.inCart', { defaultValue: 'In cart' })}
                        </span>
                      ) : (
                        <button
                          disabled={isAddingNotFound}
                          onClick={async () => {
                            flushSync(() => {
                              setAddingParts(prev => new Set(prev).add(notFoundKey));
                            });
                            try {
                              await onAddToCart({ partNumber: part, description: part, not_found: true, translated_description: translationsMap[part] || null }, structuredData);
                            } finally {
                              setAddingParts(prev => { const next = new Set(prev); next.delete(notFoundKey); return next; });
                            }
                          }}
                          className="hover:opacity-90 transition-opacity"
                          style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '11px', fontWeight: 600, color: '#6866ff', backgroundColor: 'transparent', border: '1px solid #6866ff', borderRadius: '6px', padding: '4px 8px', whiteSpace: 'nowrap', cursor: isAddingNotFound ? 'not-allowed' : 'pointer', opacity: isAddingNotFound ? 0.6 : 1 }}
                        >
                          {isAddingNotFound ? <Loader2 size={13} className="animate-spin" /> : <ShoppingCart size={13} />}
                          {isAddingNotFound
                            ? t('chat.multiPart.adding', { defaultValue: 'Adding...' })
                            : t('chat.multiPart.addToCart', { defaultValue: 'Add to cart' })}
                        </button>
                      );
                    })()}
                  </div>
                </div>
              </React.Fragment>
            );
          })}
          {unresolvedWithSuggestions.map((term, idx) => (
            <div key={`unres-${idx}`} style={{ padding: '8px 12px' }}>
              {renderCardSuggestions(suggestionsFor(term), term)}
            </div>
          ))}
        </div>
        );
      })()}

      {/* Table view — compact, scannable. A miss-only turn shows the compact
          list above instead, and only opens the table when the customer asks
          for the rest of the order. */}
      {(missOnly ? showHistory : viewMode === 'table') && renderTable()}

      {/* "You might also need" — complementary suggestions, shown under the table
          too (not just in the cards view). Aggregated + de-duped across every
          result so the table view gets a single section. */}
      {viewMode === 'table' && onAddToCart && (() => {
        const seen = new Set<string>();
        // De-dupe by name first so malformed duplicate IDs cannot collapse two
        // distinct catalog-backed suggestions into one row.
        const allSuggestions = parts_results
          .flatMap((pr) => pr.complementary_parts || [])
          .filter((s: any) => {
            const name = String(s?.suggested_part_name ?? '').trim().toLowerCase();
            const id = String(s?.suggested_part_id ?? '').trim();
            const key = name || id || JSON.stringify(s);
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
          });
        // Nothing yet, but the deferred round is still expected: hold the slot
        // open. The skeleton mirrors a real row's box, so when the suggestions
        // land they cross-fade into space that was already reserved — the
        // message never grows under the reader.
        if (allSuggestions.length === 0) {
          return complementaryPending ? (
            <div style={{ padding: '4px 12px 0' }}>
              <ComplementarySkeleton rows={4} />
            </div>
          ) : null;
        }
        const allImageParts = parts_results.flatMap((pr) => pr.image_parts || []);
        const withDiagram = parts_results.find((pr) => pr.complementary_parts && pr.complementary_parts.length > 0);
        return (
          <div style={{ padding: '4px 12px 0' }}>
            <QuerySearchView
              suggestions={allSuggestions}
              // Whole-order section: every requested part's suggestions merged
              // into one list, so it gets a higher cap than a single card's.
              maxVisible={8}
              imageParts={allImageParts.length ? allImageParts : null}
              onAddToCart={onAddToCart}
              cartPartIdentifiers={cartPartIdentifiers}
              structuredData={structuredData}
              diagramImage={withDiagram?.image || null}
              imageCallouts={withDiagram?.image_callout || null}
              createHighlightedImage={createHighlightedImage}
              onShowOnMap={(suggestion) => {
                const targetIndex = parts_results.findIndex((pr) =>
                  pr.image_parts?.some((p: any) =>
                    String(p.partId) === String(suggestion.suggested_part_id) ||
                    String(p.partNumber) === String(suggestion.suggested_part_id) ||
                    String(p.part_id) === String(suggestion.suggested_part_id) ||
                    String(p.crossCatKey) === String(suggestion.suggested_part_id)
                  )
                );
                if (targetIndex >= 0) setZoomPart({
                  partResult: parts_results[targetIndex],
                  index: targetIndex,
                  focusPartId: suggestion.suggested_part_id,
                });
              }}
            />
          </div>
        );
      })()}

      {/* Parts list — results for the same requested part are grouped under a
          highlighted "pick one" banner (see partGroups). */}
      {viewMode === 'cards' && (
      <div className="flex flex-col gap-3 stagger">
        {partGroups.map((group) => {
          const renderCard = (partResult: PartResult, index: number) => {
          const hasValidImage = partResult.image && isValidImageData(partResult.image);
          const uniqueKey = `${messageId}-part-${index}`;

          // Resolve hero part from image_parts
          let heroPart: any = null;
          if (partResult.image_parts && Array.isArray(partResult.image_parts) && partResult.part_callout_id) {
            heroPart = partResult.image_parts.find((p: any) =>
              (p.crossCatKey == partResult.part_callout_id ||
              String(p.index) === partResult.part_callout_id ||
              p.calloutLabel === partResult.part_callout_id ||
              String(p.callout) === partResult.part_callout_id ||
              p.part_callout_id === partResult.part_callout_id) &&
              (partResult.partId == (p.partId || p.part_number || p.crossCatKey || ''))
            );
          }

          const pd = partResult.structured_data?.part_data;
          const partTitle = partResult.related_user_query || heroPart?.description || heroPart?.name || pd?.description || '';
          // Raw identity vs masked display — see the table row above.
          const partNumberRaw = String(heroPart?.partNumber || heroPart?.part_number || pd?.part_number || '');
          const partNumber = displayPartRef(partNumberRaw);
          const category = heroPart?.category || pd?.category || '';
          const oeNumber = displayPartRef(heroPart?.oe_number || pd?.oe_number || pd?.category || '');
          const fromDate = heroPart?.from || pd?.from || '';
          const diagram = heroPart?.diagram || pd?.diagram || '';
          // Catalog quantity — the top-level `quantity` the cart line is seeded
          // from (falls back to the legacy `.qty`); defaults to 1 (parseCatalogQuantity).
          const qty = parseCatalogQuantity(heroPart?.quantity ?? (partResult as any).quantity ?? heroPart?.qty ?? pd?.qty);
          const price = heroPart?.pricing_data || heroPart?.pricing_data || pd?.pricing_data || '';
          const description = heroPart?.description || heroPart?.name || pd?.description || '';
          const confidence = heroPart?.confidence ?? partResult.structured_data?.confidence ?? null;
          // Side/position (Left/Right…) from an "L/R"-style extra_details column —
          // shown as a detail row below, not in the summary above the diagram.
          const positionChips = positionChipsFromExtra((partResult as any)?.extra_details);

          // Resolved display label + warning-capped probability (shared builder —
          // also used by the header row below and the parts tracker panel).
          const {
            label: headerLabel,
            hasWarnings: headerHasWarnings,
            probability: headerProbability,
          } = buildResolvedPartHeader(partResult, partTitle);
          // The card header reads its line's label — the same string the tracker
          // row and the table row show.
          const resolvedLabel = labelForCard(partResult, headerLabel);

          // Cart status — OE part number only (matches the table via oeInCart).
          const isInCart = oeInCart(partNumberRaw, heroPart?.oe_number, pd?.oe_number, partResult.partId);

          // The cart "search" badge shows the ORIGINAL term the user typed for
          // this part (e.g. "Bumper Diffuser, Rear"), NOT the full resolved card
          // label (which appends the translation + found-as name). Keep this in
          // sync with the "Add All" handler so single-add and bulk-add agree.
          const originalQuery = String(partResult.related_user_query ?? heroPart?.related_user_query ?? '');

          // Add to cart handler for individual part
          const handleAddSingleToCart = async () => {
            if (!onAddToCart) return;
            flushSync(() => {
              setAddingParts(prev => new Set(prev).add(uniqueKey));
            });
            try {
              let imageToUse = partResult.image;
              if (partResult.image && partResult.image_callout && Array.isArray(partResult.image_callout) && partResult.part_callout_id) {
                const matchingCallout = partResult.image_callout.find(c => c.label === partResult.part_callout_id);
                if (matchingCallout) {
                  imageToUse = await createHighlightedImage(partResult.image, partResult.image_callout, partResult.part_callout_id);
                }
              }
              // Build a PER-PART structuredData carrying this part's own search
              // query, instead of the message-level structuredData (which has no
              // per-part query). This is what populates the cart search badge.
              const perPartStructuredData = {
                vin_data: partResult.structured_data?.vin_data ?? structuredData?.vin_data ?? {
                  make: '', manufacturer: '', model: '', trim: '', vin: '', year: '',
                },
                part_data: {
                  // OE part number FIRST — crossCatKey is the diagram callout,
                  // shared by left/right pairs (see handleAddRowToCart).
                  part_id: String(partNumberRaw || partResult.partId || heroPart?.crossCatKey || ''),
                  part_number: String(partNumberRaw || 'N/A'),
                  description: String(description || ''),
                  price: price ?? '--',
                  query: originalQuery,
                },
                selection_reason: String(description || 'Selected from multi-part results'),
              };
              await onAddToCart({
                partNumber: partNumberRaw,
                partId: partNumberRaw || partResult.partId || heroPart?.crossCatKey,
                description,
                price,
                image: imageToUse,
                related_user_query: originalQuery || resolvedLabel || partResult.related_user_query,
                translated_description: translationsMap[description] || null,
                quantity: (heroPart as any)?.quantity ?? (partResult as any).quantity,
              }, perPartStructuredData);
            } finally {
              setAddingParts(prev => { const next = new Set(prev); next.delete(uniqueKey); return next; });
            }
          };
          const isAddingSingle = addingParts.has(uniqueKey);

          // Check if this part was already shown in an earlier message
          // The anchor map is keyed on RAW part numbers (see duplicatePartAnchors),
          // so look up with the raw value — "•••" would never match, and in guest
          // mode it would match every part at once.
          const duplicateAnchor = partNumberRaw
            ? duplicatePartAnchors.get(partNumberRaw.toLowerCase())
            : undefined;

          return (
            <div
              key={uniqueKey}
              id={uniqueKey}
              className="w-full h-full overflow-hidden"
              style={{ backgroundColor: '#f6f4f0', borderRadius: '10px', border: '1px solid #ebe7df' }}
            >
              {/* "Brought before" notice for duplicate parts */}
              {duplicateAnchor && (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: '6px',
                  padding: '7px 12px',
                  backgroundColor: '#fef9ec',
                  borderBottom: '1px solid #f0e8c9',
                  borderRadius: '10px 10px 0 0',
                }}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 20 20" fill="#92600a" style={{ flexShrink: 0 }}>
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <span style={{ fontSize: '11px', color: '#92600a', fontWeight: 500 }}>
                    {t('chat.multiPart.alreadyBroughtBefore', { defaultValue: 'This part was already brought before' })}
                    {' — '}
                    <a
                      href={`#${duplicateAnchor}`}
                      onClick={(e) => {
                        e.preventDefault();
                        const el = document.getElementById(duplicateAnchor);
                        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                      }}
                      style={{ color: '#6866ff', fontWeight: 600, textDecoration: 'underline', cursor: 'pointer' }}
                    >
                      {t('chat.multiPart.scrollToPart', { defaultValue: 'see here' })}
                    </a>
                  </span>
                </div>
              )}
              {/* Part title + assumption tooltip */}
              {resolvedLabel && (
                <div style={{ padding: '10px 12px 6px 12px' }}>
                  {(() => {
                    // Assumption data is now directly on partResult — no fuzzy matching needed.
                    // Label + capped probability come from the shared header builder (computed
                    // at card scope) so the parts tracker panel renders the exact same values.
                    // Using resolvedLabel as condition (not partTitle) ensures part_parameters
                    // are always included in the display, matching PartsTrackerPanel behavior.
                    // Our reading of the request leads the card header; the
                    // customer's original words sit on the Ref line.
                    const { partName, ref } = splitPartAndRef(resolvedLabel);
                    return (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '5px', flexWrap: 'wrap' }}>
                          <h3 style={{ color: '#201e1a', fontSize: '13px', fontWeight: 600, lineHeight: '18px', margin: 0 }}>
                            <span style={{ color: '#9a958c', fontWeight: 600 }}>{t('chat.multiPart.partLabel', { defaultValue: 'Part' })}: </span>
                            {partName}
                          </h3>
                          {headerProbability != null && !partResult.user_selected && (
                            <ProbabilityBadge probability={headerProbability} t={t} />
                          )}
                          {/* Follows the probability, mirroring the table's
                              PROB. column — the two read as one statement. */}
                          {partResult.user_selected && userSelectedBadge}
                          {headerHasWarnings && (
                            <AssumptionTooltip lines={partResult.resolutions ?? []} resolutions_explanation={partResult.resolutions_explanation} resolution_keys={partResult.resolution_keys} />
                          )}
                          {canEditResult && (
                            <button
                              type="button"
                              onClick={() => editResult(ref || originalQuery || partName, partName)}
                              title={t('chat.partEdit.resultAction', { defaultValue: 'Edit this search' })}
                              style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '3px 7px', fontSize: '10px', fontWeight: 600, color: '#6866ff', backgroundColor: '#f4f2ff', border: '1px solid #ddd9ff', borderRadius: '7px', cursor: 'pointer' }}
                            >
                              <Pencil size={10} /> {t('chat.partEdit.resultActionShort', { defaultValue: 'Edit search' })}
                            </button>
                          )}
                        </div>
                        {ref && (
                          <div style={{ fontSize: '12px', color: '#6b6b6b', lineHeight: '16px' }}>
                            <span style={{ color: '#9a958c', fontWeight: 600 }}>{t('chat.multiPart.refLabel', { defaultValue: 'Ref' })} : </span>
                            ({ref})
                          </div>
                        )}
                        {/* Link to the user's own (annotated) photo this part was
                            identified in — vision turns only. */}
                        {(() => {
                          const vurl = visionImageFor(ref, partName, originalQuery);
                          return vurl ? (
                            <a
                              href={vurl}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={e => e.stopPropagation()}
                              style={{ alignSelf: 'flex-start', display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: '11px', fontWeight: 600, color: '#6866ff', textDecoration: 'none' }}
                              title={t('chat.multiPart.yourPhotoTitle', { defaultValue: 'Open the photo this part was identified in' })}
                            >
                              <ZoomIn size={12} /> {t('chat.multiPart.yourPhoto', { defaultValue: 'Your photo' })}
                            </a>
                          ) : null;
                        })()}
                      </div>
                    );
                  })()}
                  {/* Resolved-card reasoning stays behind the score's info icon.
                      Clarification cards keep their explanatory hint inline. */}
                  {/* Transparency: other catalog candidates the agent weighed but did not pick */}
                  <AlternativesTable alternatives={partResult.alternatives} t={t} />
                  {/* Why this part: the four indications from the photo (Stage 3b). */}
                  <MatchEvidencePanel
                    evidence={partResult.match_evidence}
                    partId={partResult.partId}
                    partName={partResult.part_name || partResult.related_corrected_name || ''}
                    photoUrl={visionImageFor(partResult.related_user_query, partResult.related_corrected_name, partResult.original_name)}
                    messageId={messageId}
                  />
                  <ExpertDecisionReceipt line={lineByCard.get(index)} t={t} />
                </div>
              )}

              {/* OE Notes & From — the SAME fields the table shows (remark/notes/
                  optional package, and the availability date), for parity between views. */}
              {(() => {
                const notes = partNoteValues(partResult);
                const fromVal = partFromValue(partResult);
                if (notes.length === 0 && !fromVal) return null;
                return (
                  <div style={{ padding: '0 12px 8px 12px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    {notes.length > 0 && (
                      <div style={{ fontSize: '12px', color: '#6b6b6b', lineHeight: '16px', overflowWrap: 'break-word', wordBreak: 'normal' }}>
                        <span style={{ color: '#9a958c', fontWeight: 600 }}>{t('chat.multiPart.oeNotes', { defaultValue: 'OE Notes' })}: </span>
                        {notes.map((n, i) => {
                          const tn = translationsMap[n];
                          const isTrans = !!tn && tn.trim() !== n.trim();
                          return (
                            <span key={i}>{i > 0 ? '; ' : ''}{tn || n}{isTrans && <> {translatedBadge}</>}</span>
                          );
                        })}
                      </div>
                    )}
                    {fromVal && (
                      <div style={{ fontSize: '12px', color: '#6b6b6b', lineHeight: '16px' }}>
                        <span style={{ color: '#9a958c', fontWeight: 600 }}>{t('chat.multiPart.from', { defaultValue: 'From' })}: </span>
                        {fromVal}
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Image */}
              {hasValidImage && partResult.image && (
                <div style={{ padding: '0 12px 8px 12px' }}>
                  <div className="overflow-hidden" style={{ backgroundColor: '#ffffff', borderRadius: '10px' }}>
                    {(partResult.image_callout && partResult.image_callout.length > 0) || (partResult.image_parts && partResult.image_parts.length > 0) ? (
                      <ImageWithCallouts
                        imageData={partResult.image}
                        calloutData={partResult.image_callout || []}
                        messageId={uniqueKey}
                        partInfo={partResult}
                        partCalloutId={partResult.part_callout_id}
                        partId={partNumber || ''}
                        youMayLikeParts={partResult.complementary_parts || []}
                        image_parts={partResult.image_parts || null}
                        vin_data={partResult.structured_data?.vin_data || null}
                        onOrderUpdate={onOrderUpdate}
                        orderUpdateTrigger={orderUpdateTrigger}
                        onSearchPart={directSearch}
                        preTranslatedDescriptions={translationsMap}
                        onAddToCart={onAddToCart}
                        resolvedLabel={resolvedLabel}
                        displayProbability={headerProbability}
                      />
                    ) : (
                      <img
                        src={partResult.image}
                        alt={t('chat.multiPart.partDiagram', { defaultValue: `Part diagram ${index + 1}`, number: index + 1 })}
                        className="w-full h-auto object-contain"
                        style={{ maxHeight: '264px' }}
                      />
                    )}
                  </div>
                </div>
              )}

              {/* Status badges, details table, and Add to Cart button are only shown
                  when ImageWithCallouts is NOT used — it already renders its own
                  details panel and add-to-cart button below the image. */}
              {!partResult.image_callout && (
                <>
                  {/* Status badges (Selected / In Order + confidence) */}
                  {(isInCart || confidence !== null) && (
                    <div style={{ padding: '8px 12px 6px 12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      {isInCart ? (
                        <span style={{ display: 'inline-flex', alignItems: 'center', padding: '2px 8px', fontSize: '11px', fontWeight: 600, lineHeight: '16px', backgroundColor: '#c7ffe8', color: '#137f67', borderRadius: '40px' }}>
                          {t('chat.multiPart.inOrder', { defaultValue: 'In Order' })}
                        </span>
                      ) : (
                        <span style={{ display: 'inline-flex', alignItems: 'center', padding: '2px 8px', fontSize: '11px', fontWeight: 600, lineHeight: '16px', backgroundColor: '#c3eff4', color: '#1193a2', borderRadius: '40px' }}>
                          {t('chat.multiPart.selected', { defaultValue: 'Selected' })}
                        </span>
                      )}
                      {confidence !== null && (
                        <span title={(() => { const b = matchBand(Number(confidence)) || 'low'; return t(MATCH_BAND_LABEL[b].key, { defaultValue: MATCH_BAND_LABEL[b].defaultValue }); })()} style={isInCart
                          ? { display: 'inline-flex', alignItems: 'center', padding: '2px 8px', fontSize: '11px', fontWeight: 600, lineHeight: '16px', backgroundColor: '#d3edff', color: '#1176a2', borderRadius: '40px' }
                          : { display: 'inline-flex', alignItems: 'center', padding: '2px 8px', fontSize: '11px', fontWeight: 600, lineHeight: '16px', backgroundColor: '#c3eff4', color: '#1193a2', borderRadius: '40px' }}>
                          {(() => { const b = matchBand(Number(confidence)) || 'low'; return t(MATCH_BAND_LABEL[b].key, { defaultValue: MATCH_BAND_LABEL[b].defaultValue }); })()}
                        </span>
                      )}
                    </div>
                  )}

                  {/* Part details table */}
                  {(description || category || oeNumber || partNumber || fromDate || diagram || price || positionChips.length > 0) && (
                    <div style={{ padding: '2px 12px 8px 12px', display: 'flex', flexDirection: 'column', gap: '3px' }}>
                      {positionChips.length > 0 && (
                        <div style={{ display: 'flex', gap: '0', alignItems: 'center' }}>
                          <span style={{ fontSize: '12px', fontWeight: 400, color: '#999', lineHeight: '18px', width: '72px', flexShrink: 0 }}>{t('chat.multiPart.position', { defaultValue: 'Position' })}</span>
                          <span style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                            {positionChips.map((p, i) => (
                              <span key={i} style={{ flexShrink: 0, fontSize: '10px', fontWeight: 600, color: '#3627BF', backgroundColor: '#eef0ff', border: '1px solid #d6d8ff', borderRadius: '6px', padding: '1px 6px', whiteSpace: 'nowrap' }}>
                                {tPosChip(p) !== p ? tPosChip(p) : (translationsMap[p] || p)}
                              </span>
                            ))}
                          </span>
                        </div>
                      )}
                      {description && (
                        <div style={{ display: 'flex', gap: '0' }}>
                          <span style={{ fontSize: '12px', fontWeight: 400, color: '#999', lineHeight: '18px', width: '72px', flexShrink: 0 }}>{t('chat.multiPart.description', { defaultValue: 'Description' })}</span>
                          <span style={{ fontSize: '12px', fontWeight: 600, color: '#201e1a', lineHeight: '18px' }}>{description}</span>
                        </div>
                      )}
                      {category && (
                        <div style={{ display: 'flex', gap: '0' }}>
                          <span style={{ fontSize: '12px', fontWeight: 400, color: '#999', lineHeight: '18px', width: '72px', flexShrink: 0 }}>{t('chat.multiPart.category', { defaultValue: 'Category' })}</span>
                          <span style={{ fontSize: '12px', fontWeight: 600, color: '#201e1a', lineHeight: '18px' }}>{category}</span>
                        </div>
                      )}
                      {oeNumber && (
                        <div style={{ display: 'flex', gap: '0' }}>
                          <span style={{ fontSize: '12px', fontWeight: 400, color: '#999', lineHeight: '18px', width: '72px', flexShrink: 0 }}>{t('chat.multiPart.oeNumber', { defaultValue: 'OE#' })}</span>
                          <span style={{ fontSize: '12px', fontWeight: 600, color: '#201e1a', lineHeight: '18px' }}>
                            {oeNumber}
                            <SupersessionIndicator row={{ partNumber: partNumberRaw, extra_details: supersessionDetails(heroPart, partResult) }} />
                          </span>
                        </div>
                      )}
                      {partNumber && (
                        <div style={{ display: 'flex', gap: '0' }}>
                          <span style={{ fontSize: '12px', fontWeight: 400, color: '#999', lineHeight: '18px', width: '72px', flexShrink: 0 }}>{t('chat.multiPart.partNumber', { defaultValue: 'Part #' })}</span>
                          <span style={{ fontSize: '12px', fontWeight: 600, color: '#201e1a', lineHeight: '18px' }}>{partNumber}</span>
                        </div>
                      )}
                      {fromDate && (
                        <div style={{ display: 'flex', gap: '0' }}>
                          <span style={{ fontSize: '12px', fontWeight: 400, color: '#999', lineHeight: '18px', width: '72px', flexShrink: 0 }}>{t('chat.multiPart.from', { defaultValue: 'From' })}</span>
                          <span style={{ fontSize: '12px', fontWeight: 600, color: '#201e1a', lineHeight: '18px' }}>{fromDate}</span>
                        </div>
                      )}
                      {diagram && (
                        <div style={{ display: 'flex', gap: '0' }}>
                          <span style={{ fontSize: '12px', fontWeight: 400, color: '#999', lineHeight: '18px', width: '72px', flexShrink: 0 }}>{t('chat.multiPart.diagram', { defaultValue: 'Diagram' })}</span>
                          <span style={{ fontSize: '12px', fontWeight: 600, color: '#201e1a', lineHeight: '18px' }}>{diagram}</span>
                        </div>
                      )}
                      <div style={{ display: 'flex', gap: '0' }}>
                        <span style={{ fontSize: '12px', fontWeight: 400, color: '#999', lineHeight: '18px', width: '72px', flexShrink: 0 }}>{t('chat.multiPart.qty', { defaultValue: 'Qty' })}</span>
                        <span style={{ fontSize: '12px', fontWeight: 600, color: '#201e1a', lineHeight: '18px' }}>{qty}</span>
                      </div>
                      <div style={{ display: 'flex', gap: '0' }}>
                        <span style={{ fontSize: '12px', fontWeight: 400, color: '#999', lineHeight: '18px', width: '72px', flexShrink: 0 }}>{t('chat.multiPart.price', { defaultValue: 'Price' })}</span>
                        <span style={{ fontSize: '12px', fontWeight: 600, color: '#201e1a', lineHeight: '18px' }}>{price || '—'}</span>
                      </div>
                    </div>
                  )}

                  {/* Add to Cart / Already in Cart */}
                  {onAddToCart && (
                    <div style={{ padding: isInCart ? '0 12px 8px 12px' : '0 12px 12px 12px' }}>
                      {isInCart ? (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '6px', paddingTop: '6px' }}>
                          <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 20 20" fill="#137f67">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          <span style={{ fontSize: '12px', fontWeight: 600, color: '#137f67', lineHeight: '18px' }}>
                            {t('chat.multiPart.alreadyInCart', { defaultValue: 'Already in Cart' })}
                          </span>
                        </div>
                      ) : (
                        <button
                          onClick={handleAddSingleToCart}
                          disabled={isAddingSingle}
                          className="hover:opacity-90 transition-opacity"
                          style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', fontSize: '12px', fontWeight: 600, color: '#6866ff', backgroundColor: 'transparent', border: '1px solid #6866ff', borderRadius: '8px', height: '34px', cursor: isAddingSingle ? 'not-allowed' : 'pointer', lineHeight: '18px', opacity: isAddingSingle ? 0.6 : 1 }}
                        >
                          {isAddingSingle ? (
                            <>
                              <svg className="animate-spin" style={{ width: '13px', height: '13px', flexShrink: 0 }} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              {t('chat.multiPart.adding', { defaultValue: 'Adding...' })}
                            </>
                          ) : (
                            <>
                              <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 20 20" fill="currentColor">
                                <path d="M3 1a1 1 0 000 2h1.22l.305 1.222a.997.997 0 00.01.042l1.358 5.43-.893.892C3.74 11.846 4.632 14 6.414 14H15a1 1 0 000-2H6.414l1-1H14a1 1 0 00.894-.553l3-6A1 1 0 0017 3H6.28l-.31-1.243A1 1 0 005 1H3zM16 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM6.5 18a1.5 1.5 0 100-3 1.5 1.5 0 000 3z" />
                              </svg>
                              {t('chat.multiPart.addToCart', { defaultValue: 'Add to Cart' })}
                            </>
                          )}
                        </button>
                      )}
                    </div>
                  )}
                </>
              )}

              {/* Complementary parts suggestions */}
              {partResult.complementary_parts && partResult.complementary_parts.length > 0 && onAddToCart && (
                <div style={{ padding: '0 12px 12px 12px' }}>
                  <QuerySearchView
                    suggestions={partResult.complementary_parts}
                    imageParts={partResult.image_parts || null}
                    onAddToCart={onAddToCart}
                    cartPartIdentifiers={cartPartIdentifiers}
                    structuredData={structuredData}
                    diagramImage={partResult.image || null}
                    imageCallouts={partResult.image_callout || null}
                    createHighlightedImage={createHighlightedImage}
                    onShowOnMap={(suggestion) => setZoomPart({
                      partResult,
                      index,
                      focusPartId: suggestion.suggested_part_id,
                    })}
                  />
                </div>
              )}
            </div>
          );
          };

          // Single result → render the card directly.
          if (group.indices.length <= 1) {
            return renderCard(parts_results[group.indices[0]], group.indices[0]);
          }

          // Multiple cards mapped to one requested part. This should only happen
          // for an explicitly MERGED "X and Y" request (e.g. bumper upper AND
          // lower), where the user wants BOTH — the search agent (Rule ZERO) never
          // emits two cards for a single ambiguous component; the rejected
          // candidates live in each card's `alternatives` instead. So we present
          // these as complementary variants that were BOTH added — never as a
          // "pick the right one" choice the user has to resolve.
          return (
            <div
              key={`group-${group.key}`}
              style={{
                borderRadius: '12px',
                border: '1px solid #d6f5e4',
                backgroundColor: '#f5fffa',
                padding: '8px',
                display: 'flex', flexDirection: 'column', gap: '8px',
              }}
            >
              <div style={{
                display: 'flex', alignItems: 'flex-start', gap: '7px',
                padding: '7px 10px', borderRadius: '8px',
                backgroundColor: '#e6f9f0', border: '1px solid #b8ecd2',
              }}>
                <span aria-hidden="true" style={{ fontSize: '13px', lineHeight: '16px', flexShrink: 0 }}>✓</span>
                <span style={{ fontSize: '11px', fontWeight: 600, color: '#137f67', lineHeight: '16px' }}>
                  {t('chat.multiPart.multipleResultsForOne', {
                    defaultValue: 'Both variants added for "{{name}}".',
                    count: group.indices.length,
                    name: group.label,
                  })}
                </span>
              </div>
              {group.indices.map((idx) => renderCard(parts_results[idx], idx))}
            </div>
          );
        })}
      </div>
      )}

      {/* Add all button */}
      {onAddToCart && allAddableItems.length > 1 && (
        <div className="flex justify-end pt-1">
          <button
            onClick={handleAddAllToCart}
            disabled={allInCart || isAddingAll}
            className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold transition-all duration-200"
            style={
              allInCart
                ? { backgroundColor: '#c7ffe8', color: '#137f67', cursor: 'default', borderRadius: '10px' }
                : isAddingAll
                  ? { backgroundColor: '#e5e2ff', color: '#6866ff', cursor: 'wait', borderRadius: '10px' }
                  : { backgroundColor: '#6866ff', color: '#ffffff', borderRadius: '10px' }
            }
          >
            {allInCart ? (
              <>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                {t('chat.multiPart.allInCart', { defaultValue: 'Added' })}
              </>
            ) : isAddingAll ? (
              <>
                <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {t('chat.multiPart.addingAll', { defaultValue: 'Adding...' })}
              </>
            ) : (
              <>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M3 1a1 1 0 000 2h1.22l.305 1.222a.997.997 0 00.01.042l1.358 5.43-.893.892C3.74 11.846 4.632 14 6.414 14H15a1 1 0 000-2H6.414l1-1H14a1 1 0 00.894-.553l3-6A1 1 0 0017 3H6.28l-.31-1.243A1 1 0 005 1H3zM16 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM6.5 18a1.5 1.5 0 100-3 1.5 1.5 0 000 3z" />
                </svg>
                {t('chat.multiPart.addAllToCart', { defaultValue: 'Add all ({{count}})', count: allAddableItems.length })}
              </>
            )}
          </button>
        </div>
      )}

      {/* Image zoom — clicking a table thumbnail opens that part's REGULAR
          ImageWithCallouts zoom modal (same as the cards/original view). The
          component is mounted hidden; it portals its own fullscreen modal. */}
      {zoomPart && (() => {
        const { partResult, index, focusPartId } = zoomPart;
        const v = derivePartView(partResult, index);
        return (
          <div style={{ display: 'none' }}>
            <ImageWithCallouts
              autoOpenModal
              onModalClose={() => setZoomPart(null)}
              imageData={partResult.image || ''}
              calloutData={partResult.image_callout || []}
              messageId={`zoom-${v.uniqueKey}`}
              partInfo={partResult}
              partCalloutId={partResult.part_callout_id}
              partId={focusPartId || v.partNumber || ''}
              youMayLikeParts={partResult.complementary_parts || []}
              image_parts={partResult.image_parts || null}
              vin_data={partResult.structured_data?.vin_data || null}
              onOrderUpdate={onOrderUpdate}
              orderUpdateTrigger={orderUpdateTrigger}
              onSearchPart={directSearch}
              preTranslatedDescriptions={translationsMap}
              onAddToCart={onAddToCart}
              resolvedLabel={v.resolvedLabel}
            />
          </div>
        );
      })()}

    </div>
  );
};

export default MultiPartResultView;
